/* assets/ids-quickscan.js
   IdenScan — QuickScan (landing + Stripe return results) — v5.0.0
   Safe upgrade of v4.4.2

   Doel (behouden):
   - Landing: e-mail verzamelen -> Stripe redirect
   - Result page: na Stripe return (?session_id=...) EXACT 1x triggeren (per session)
   - Grote rapporten betrouwbaar ophalen
   - Retry = POLL-ONLY (geen nieuwe scan-starts)
   - Trigger ACK + server job_id opslaan en hergebruiken bij refresh/retry

   v5.0.0 upgrades:
   - Result modals: open/close + focus restore + clean 1x auto-open success offer
   - Gift promo copy button support via data-ids-copy-btn + data-ids-copy-value (data-copied=1 UI state)
   - Stronger no-match handling (hide empty breaches card when truly clean)
   - Better severity badge labels in human Dutch
   - Username meta rendering
   - Playbook rendering via data-qs-playbook-host / template[data-qs-playbook-item]
   - Generic findings fallback when phone/username hits exist but no breach cards are provided

   v5.0.1 analytics patch:
   - Canonieke GA4 purchase via gtag('event', 'purchase', ...)
   - Exact 1x per echte Stripe session_id
   - Geen purchase in testmode
   - Geen wijzigingen aan webhook/poll/cache/result flow behalve analytics firing
*/

(function () {
  "use strict";

  // ------------------------------------------------------------
  // GLOBAL INIT GUARD
  // ------------------------------------------------------------
  if (window.__IDS_QS_BOOTED__) return;
  window.__IDS_QS_BOOTED__ = true;

  // ------------------------------------------------------------
  // CONSTANTS
  // ------------------------------------------------------------
  var STORAGE = {
    email: "ids_qs_email",
    intent: "ids_qs_intent",
    startedAt: "ids_qs_started_at",
    resultsAbsUrl: "ids_qs_results_abs_url",
    makeUrl: "ids_qs_make_url",
    testMode: "ids_qs_test_mode",
    forceTrigger: "ids_qs_force_trigger",

    // Existing additive keys
    phone: "ids_qs_phone",
    username: "ids_qs_username",

    // Make-gating depends on this
    idempotencyKey: "ids_qs_idempotency_key",
    consentBool: "ids_qs_consent_bool",

    // namespaced session keys
    sessionLockPrefix: "ids_qs_lock_",        // + sessionId
    sessionDonePrefix: "ids_qs_done_",        // + sessionId
    sessionCachePrefix: "ids_qs_cache_",      // + sessionId
    sessionMetaPrefix: "ids_qs_meta_",        // + sessionId
    sessionAckPrefix: "ids_qs_ack_",          // + sessionId
    sessionUiPrefix: "ids_qs_ui_",            // + sessionId + "_" + flag
    sessionGaPurchasePrefix: "ids_qs_ga_purchase_" // + sessionId
  };

  var CACHE_SCHEMA_VERSION = "qs_cache_v5_0_0";

  var TIMING = {
    LOCK_TTL_MS: 10 * 60 * 1000,         // 10 min
    TRIGGER_ACK_TTL_MS: 24 * 60 * 60 * 1000, // 24h

    // intent/PII hygiene
    INTENT_TTL_MS: 48 * 60 * 60 * 1000,  // 48h: daarna pending intent + extras opruimen

    PROGRESS_EXPECTED_MS: 120 * 1000,
    POLL_SOFT_MS: 180 * 1000,
    POLL_TOTAL_MS: 12 * 60 * 1000,

    POLL_INTERVAL_MS: 5000, // legacy (niet als base gebruikt)
    STATUS_ROTATE_MS: 8000
  };

  var QS_UI_STATES = {
    IDLE: "idle",
    PAID: "paid",
    SCANNING: "scanning",
    PROCESSING: "processing",
    DONE: "done",
    ERROR: "error"
  };

  // ------------------------------------------------------------
  // UTILS
  // ------------------------------------------------------------
  function qs(root, sel) {
    try {
      return root ? root.querySelector(sel) : null;
    } catch (e) {
      return null;
    }
  }

  function qsa(root, sel) {
    try {
      return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
    } catch (e) {
      return [];
    }
  }

  function byId(id) {
    if (!id) return null;
    try {
      return document.getElementById(id);
    } catch (e) {
      return null;
    }
  }

  function text(el, value) {
    if (!el) return;
    el.textContent = value == null ? "" : String(value);
  }

  function html(el, value) {
    if (!el) return;
    el.innerHTML = value == null ? "" : String(value);
  }

  function show(el) {
    if (!el) return;
    el.hidden = false;
    try {
      el.removeAttribute("hidden");
    } catch (e) {}
  }

  function hide(el) {
    if (!el) return;
    el.hidden = true;
    try {
      el.setAttribute("hidden", "hidden");
    } catch (e) {}
  }

  function setAttr(el, name, value) {
    if (!el) return;
    try {
      el.setAttribute(name, String(value));
    } catch (e) {}
  }

  function removeAttr(el, name) {
    if (!el) return;
    try {
      el.removeAttribute(name);
    } catch (e) {}
  }

  function isObj(v) {
    return !!v && typeof v === "object" && !Array.isArray(v);
  }

  function now() {
    return Date.now();
  }

  function safeNowMs() {
    return Date.now();
  }

  function safeJSONParse(input, fallback) {
    if (typeof input !== "string") return fallback;
    try {
      return JSON.parse(input);
    } catch (e) {
      return fallback;
    }
  }

  function safeParseAnyJsonString(input) {
    if (typeof input !== "string") return null;
    var s = input.trim();
    if (!s) return null;

    if (
      (s.charAt(0) === "{" && s.charAt(s.length - 1) === "}") ||
      (s.charAt(0) === "[" && s.charAt(s.length - 1) === "]")
    ) {
      var direct = safeJSONParse(s, null);
      if (direct != null) return direct;
    }

    var start = s.indexOf("{");
    var end = s.lastIndexOf("}");
    if (start >= 0 && end > start) {
      var maybe = s.slice(start, end + 1);
      var parsed = safeJSONParse(maybe, null);
      if (parsed != null) return parsed;
    }

    return null;
  }

  function tryLocalStorageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function tryLocalStorageSet(key, value) {
    try {
      localStorage.setItem(key, String(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function tryLocalStorageRemove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }

  function trySessionStorageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function trySessionStorageSet(key, value) {
    try {
      sessionStorage.setItem(key, String(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function trySessionStorageRemove(key) {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }

  // “dual” storage for critical keys
  function dualGet(key) {
    return trySessionStorageGet(key) || tryLocalStorageGet(key);
  }

  function dualSet(key, value) {
    var a = trySessionStorageSet(key, value);
    var b = tryLocalStorageSet(key, value);
    return a || b;
  }

  function dualRemove(key) {
    trySessionStorageRemove(key);
    tryLocalStorageRemove(key);
  }

  function storageKey(prefix, id) {
    return String(prefix || "") + String(id || "");
  }

  function uiFlagKey(sessionId, flag) {
    return storageKey(STORAGE.sessionUiPrefix, String(sessionId || "") + "_" + String(flag || ""));
  }

  function readUiFlag(sessionId, flag) {
    return trySessionStorageGet(uiFlagKey(sessionId, flag)) || tryLocalStorageGet(uiFlagKey(sessionId, flag));
  }

  function writeUiFlag(sessionId, flag) {
    trySessionStorageSet(uiFlagKey(sessionId, flag), "1");
    tryLocalStorageSet(uiFlagKey(sessionId, flag), "1");
  }

  function isValidEmail(v) {
    v = (v || "").trim();
    if (!v || v.length < 5 || v.length > 254) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  function normalizePhone(v) {
    var s = String(v == null ? "" : v).trim();
    if (!s) return "";
    s = s.replace(/[^\d+]/g, "");
    if (s.indexOf("+") > 0) s = s.replace(/\+/g, "");
    if (s.length < 6 || s.length > 20) return "";
    return s;
  }

  function normalizeUsername(v) {
    var s = String(v == null ? "" : v).trim();
    if (!s) return "";
    if (s.length < 2 || s.length > 50) return "";
    return s;
  }

  function maskEmail(email) {
    email = String(email || "").trim();
    if (!email || email.indexOf("@") === -1) return "—";
    var parts = email.split("@");
    var local = parts[0] || "";
    var domain = parts.slice(1).join("@") || "";
    if (!domain) return "—";
    return (local.charAt(0) || "*") + "***@" + domain;
  }

  function maskPhone(phone) {
    var s = String(phone || "").trim();
    if (!s) return "—";
    var digits = s.replace(/[^\d]/g, "");
    if (digits.length <= 3) return "***";
    var tail = digits.slice(-3);
    return (s.charAt(0) === "+" ? "+" : "") + "***" + tail;
  }

  function maskUsername(u) {
    var s = String(u || "").trim();
    if (!s) return "—";
    if (s.length <= 2) return s.charAt(0) + "*";
    return s.charAt(0) + "***";
  }

  function toArray(v) {
    if (Array.isArray(v)) return v;
    if (v == null || v === "") return [];
    return [v];
  }

  function toStringArray(v) {
    return toArray(v)
      .map(function (x) {
        return String(x == null ? "" : x).trim();
      })
      .filter(Boolean);
  }

  function uniqueStrings(arr) {
    var out = [];
    var seen = Object.create(null);
    toStringArray(arr).forEach(function (item) {
      var key = item.toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(item);
    });
    return out;
  }

  function firstNonEmpty() {
    for (var i = 0; i < arguments.length; i++) {
      var v = arguments[i];
      if (v == null) continue;
      var s = String(v).trim();
      if (s) return s;
    }
    return "";
  }

  function clamp(n, min, max) {
    n = Number(n);
    if (!isFinite(n)) return min;
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }

  function validHttpUrl(url) {
    if (!url || typeof url !== "string") return false;
    try {
      var u = new URL(url, window.location.origin);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch (e) {
      return false;
    }
  }

  function normalizeAbsUrlLoose(url) {
    if (!url || typeof url !== "string") return "";
    try {
      var u = new URL(url, window.location.origin);
      return u.origin + u.pathname.replace(/\/+$/, "");
    } catch (e) {
      return String(url || "").trim().replace(/\/+$/, "");
    }
  }

  function sameEndpointUrl(a, b) {
    var na = normalizeAbsUrlLoose(a);
    var nb = normalizeAbsUrlLoose(b);
    return !!na && !!nb && na === nb;
  }

  function asBool(v) {
    if (v === true) return true;
    if (v === false) return false;
    var s = String(v == null ? "" : v).trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes" || s === "on";
  }

  function formatDateNL(dateStr) {
    if (!dateStr) return "—";
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    try {
      return new Intl.DateTimeFormat("nl-NL", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      }).format(d);
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function latestDate(dates) {
    var best = null;
    var i;
    for (i = 0; i < dates.length; i++) {
      var s = dates[i];
      if (!s) continue;
      var d = new Date(s);
      if (isNaN(d.getTime())) continue;
      if (!best || d.getTime() > best.getTime()) best = d;
    }
    return best;
  }

  function pushDL(eventName, payload) {
    try {
      if (!window.dataLayer || typeof window.dataLayer.push !== "function") return;
      window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
    } catch (e) {}
  }

  // ------------------------------------------------------------
  // GA4 PURCHASE HELPERS (analytics-only patch)
  // ------------------------------------------------------------
  function parseMoneyToNumber(input) {
    if (typeof input === "number" && isFinite(input) && input > 0) return input;

    var s = String(input == null ? "" : input).trim();
    if (!s) return NaN;

    s = s.replace(/\s+/g, "").replace(/[^\d,.\-]/g, "");

    if (s.indexOf(",") >= 0 && s.indexOf(".") >= 0) {
      if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
        s = s.replace(/\./g, "").replace(",", ".");
      } else {
        s = s.replace(/,/g, "");
      }
    } else if (s.indexOf(",") >= 0) {
      s = s.replace(",", ".");
    }

    var n = Number(s);
    return isFinite(n) && n > 0 ? n : NaN;
  }

  function resolveQuickScanCurrency(cfg) {
    var prices = (cfg && cfg.prices) || {};
    var currency = String(
      prices.currency ||
      cfg.currency ||
      "EUR"
    ).trim().toUpperCase();

    return currency || "EUR";
  }

  function resolveQuickScanValue(cfg) {
    var prices = (cfg && cfg.prices) || {};
    var candidates = [
      prices.one_time_value,
      prices.one_time_amount,
      cfg.one_time_price_value,
      cfg.one_time_price_amount,
      prices.one_time,
      cfg.one_time_price
    ];

    for (var i = 0; i < candidates.length; i++) {
      var parsed = parseMoneyToNumber(candidates[i]);
      if (isFinite(parsed) && parsed > 0) return parsed;
    }

    return 2.99;
  }

  function getGaPurchaseStorageKey(sessionId) {
    return storageKey(STORAGE.sessionGaPurchasePrefix, sessionId);
  }

  function hasGaPurchaseTracked(sessionId) {
    return tryLocalStorageGet(getGaPurchaseStorageKey(sessionId)) === "1";
  }

  function markGaPurchaseTracked(sessionId) {
    tryLocalStorageSet(getGaPurchaseStorageKey(sessionId), "1");
  }

  function buildQuickScanPurchasePayload(sessionId, cfg) {
    var value = resolveQuickScanValue(cfg);
    var currency = resolveQuickScanCurrency(cfg);

    return {
      send_to: "G-2N97MFC52L",
      transaction_id: String(sessionId || ""),
      value: value,
      currency: currency,
      items: [
        {
          item_id: "quickscan",
          item_name: "IdenScan QuickScan",
          item_category: "One-time security scan",
          price: value,
          quantity: 1
        }
      ]
    };
  }

  function trackQuickScanPurchaseOnce(sessionId, cfg, testMode) {
    if (testMode) return false;
    if (!sessionId) return false;
    if (!looksLikeStripeSessionId(sessionId)) return false;
    if (hasGaPurchaseTracked(sessionId)) return false;
    if (typeof window.gtag !== "function") return false;

    try {
      var payload = buildQuickScanPurchasePayload(sessionId, cfg);
      window.gtag("event", "purchase", payload);
      markGaPurchaseTracked(sessionId);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Retry wrapper: handles the case where window.gtag is not yet available at render time.
  // Max 3 attempts at 600ms intervals. Fail-closed — localStorage dedupe prevents double-fire.
  function scheduleQsPurchase(sessionId, cfg, testMode) {
    if (testMode) return;
    if (!sessionId) return;
    if (!looksLikeStripeSessionId(sessionId)) return;
    if (hasGaPurchaseTracked(sessionId)) return;

    var _tries = 0;
    function _attempt() {
      if (hasGaPurchaseTracked(sessionId)) return;
      if (typeof window.gtag === "function") {
        trackQuickScanPurchaseOnce(sessionId, cfg, testMode);
      } else if (_tries < 3) {
        _tries++;
        setTimeout(_attempt, 600);
      }
    }
    _attempt();
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function makeListHtml(items, emptyText) {
    var arr = toStringArray(items);
    if (!arr.length) {
      return '<p class="ids-qsr__muted">' + escapeHtml(emptyText || "Geen items beschikbaar.") + "</p>";
    }
    return (
      '<ul class="ids-qsr__list">' +
      arr
        .map(function (item) {
          return "<li>" + escapeHtml(item) + "</li>";
        })
        .join("") +
      "</ul>"
    );
  }

  function boolToForm(v) {
    return v ? "true" : "false";
  }

  function getStoredStartedAtMs() {
    var raw = tryLocalStorageGet(STORAGE.startedAt);
    var n = Number(raw);
    if (isFinite(n) && n > 0) return n;
    return safeNowMs();
  }

  // -------------------------
  // IDEMPOTENCY
  // -------------------------
  function generateIdempotencyKey(seed) {
    var s = String(seed || "").trim();
    var rand = Math.random().toString(36).slice(2, 10);
    var base = "qs_" + safeNowMs() + "_" + rand;
    if (s) base += "_" + s.replace(/[^\w-]+/g, "").slice(0, 20);
    if (base.length < 11) base += "_x" + safeNowMs();
    return base;
  }

  function getOrCreateIdempotencyKey(seed) {
    var existing = String(dualGet(STORAGE.idempotencyKey) || "").trim();
    if (existing && existing.length >= 11) return existing;
    var next = generateIdempotencyKey(seed);
    dualSet(STORAGE.idempotencyKey, next);
    return next;
  }

  function getStoredConsentBool() {
    var v = String(dualGet(STORAGE.consentBool) || "").trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes" || v === "on") return "true";
    if (v === "false" || v === "0" || v === "no" || v === "off") return "false";
    return "";
  }

  function getStableJobIdFromIdem(sessionId) {
    var idem = String(dualGet(STORAGE.idempotencyKey) || "").trim();
    if (idem && idem.length >= 11) return idem;
    return generateIdempotencyKey("sid_" + String(sessionId || "").slice(0, 18));
  }

  function toFormBody(payload) {
    var params = new URLSearchParams();
    if (!isObj(payload)) return params.toString();

    Object.keys(payload).forEach(function (key) {
      var val = payload[key];

      if (val == null) {
        params.set(key, "");
        return;
      }

      if (Array.isArray(val)) {
        if (!val.length) {
          params.set(key, "");
          return;
        }
        params.delete(key);
        val.forEach(function (item) {
          params.append(key, item == null ? "" : String(item));
        });
        return;
      }

      if (typeof val === "object") {
        params.set(key, JSON.stringify(val));
        return;
      }

      params.set(key, String(val));
    });

    return params.toString();
  }

  function isDoneLikeStatus(status) {
    var s = String(status || "").toLowerCase().trim();
    return s === "done" || s === "completed" || s === "complete" || s === "success" || s === "ok";
  }

  function basePollIntervalMs(elapsedMs) {
    elapsedMs = Number(elapsedMs) || 0;
    if (elapsedMs < 60 * 1000) return 20000;
    if (elapsedMs < 3 * 60 * 1000) return 30000;
    if (elapsedMs < 6 * 60 * 1000) return 45000;
    return 60000;
  }

  function jitterMs() {
    return Math.floor(Math.random() * 1200) - 400; // -400..+800
  }

  function computePollDelayMs(elapsedMs, errorStreak) {
    var base = basePollIntervalMs(elapsedMs);
    var delay = base + jitterMs();

    errorStreak = Number(errorStreak) || 0;
    if (errorStreak > 0) delay += Math.min(30000, errorStreak * 2000);

    return clamp(delay, 8000, 90000);
  }

  function setEmailFlags(payload, email) {
    if (!payload) return payload;
    var has = !!String(email || "").trim();
    payload.email_provided = has ? "true" : "";
    payload.email_missing = has ? "" : "true";
    return payload;
  }

  function applyBackendStatusMessage(ui, msg) {
    msg = String(msg || "").trim();
    if (!msg) return;

    setStatusTextAll(ui, msg);

    try {
      if (ui && ui.root && ui.root.dataset) {
        ui.root.dataset.qsStatusHoldUntil = String(now() + 12000);
      }
    } catch (e) {}
  }

  function isProbablyMakeWebhookUrl(url) {
    url = String(url || "").toLowerCase().trim();
    if (!url) return false;
    return url.indexOf("hook.make.com") >= 0 || url.indexOf("make.com") >= 0;
  }

  function looksLikeStripeSessionId(sid) {
    sid = String(sid || "").trim();
    if (!sid) return false;
    if (sid.indexOf("cs_test_") === 0) return true;
    if (sid.indexOf("cs_live_") === 0) return true;
    if (sid.indexOf("cs_") === 0 && sid.length >= 16) return true;
    return false;
  }

  function cleanupStaleIntentIfNeeded() {
    var started = Number(tryLocalStorageGet(STORAGE.startedAt) || 0);
    if (!isFinite(started) || started <= 0) return;

    if (now() - started > TIMING.INTENT_TTL_MS) {
      tryLocalStorageRemove(STORAGE.intent);
      tryLocalStorageRemove(STORAGE.forceTrigger);
      tryLocalStorageRemove(STORAGE.phone);
      tryLocalStorageRemove(STORAGE.username);

      dualRemove(STORAGE.idempotencyKey);
      dualRemove(STORAGE.consentBool);

      pushDL("qs_intent_stale_cleanup", {});
    }
  }

  // ------------------------------------------------------------
  // PLAYBOOK NORMALIZATION
  // ------------------------------------------------------------
  var PLAYBOOK_LIBRARY = [
    {
      key: "finance",
      match: /(creditcard|debitcard|iban|bank|payment|betaal|rekening|card|pas|ccn|bankgegevens)/i,
      title: "Betaal- en bankgegevens",
      meaning: "Wanneer betaal- of bankgegevens in een lek voorkomen, is extra controle verstandig. Reageer snel bij onbekende transacties of alerts.",
      actions: [
        "Controleer je bank- of kaarttransacties op onbekende activiteit.",
        "Zet notificaties of alerts aan bij je bank of kaartprovider.",
        "Neem direct contact op met je bank als je iets verdachts ziet."
      ],
      severity_code: "critical"
    },
    {
      key: "passwords",
      match: /(wachtwoord|password|passwords|hash|hashes|passcode|credentials|inloggegevens)/i,
      title: "Wachtwoorden en inloggegevens",
      meaning: "Als wachtwoorden of inloggegevens in een lek voorkomen, is het risico op accountmisbruik hoger — vooral als hetzelfde wachtwoord ook elders is gebruikt.",
      actions: [
        "Verander wachtwoorden die je hergebruikt direct.",
        "Gebruik voortaan unieke wachtwoorden per account.",
        "Zet 2FA aan op e-mail en je belangrijkste accounts."
      ],
      severity_code: "high"
    },
    {
      key: "email",
      match: /(e-mail|email|e-mailadres|mailadres)/i,
      title: "E-mailadres",
      meaning: "Een e-mailadres in een datalek vergroot vaak de kans op phishing, wachtwoordresets en gerichte nepberichten.",
      actions: [
        "Wees extra alert op e-mails over beveiliging, inloggen of betalingen.",
        "Open links liever via de officiële app of website zelf.",
        "Controleer of je e-mailaccount 2FA gebruikt."
      ],
      severity_code: "medium"
    },
    {
      key: "phone",
      match: /(telefoon|phone|mobiel|mobile|gsm|nummer|telefoonnummer)/i,
      title: "Telefoonnummer",
      meaning: "Als een telefoonnummer in een lek voorkomt, kun je meer verdachte sms’jes, belpogingen of accountverificatie-misbruik zien.",
      actions: [
        "Wees alert op verdachte sms’jes en onverwachte verificatiecodes.",
        "Klik niet op links in stressvolle of onverwachte berichten.",
        "Controleer belangrijke accounts op sterke inlogbeveiliging."
      ],
      severity_code: "medium"
    },
    {
      key: "username",
      match: /(gebruikersnaam|username|user name|nickname|screenname|accountnaam)/i,
      title: "Gebruikersnaam",
      meaning: "Een gelekte gebruikersnaam kan worden gebruikt om accounts te koppelen of om phishing overtuigender te maken.",
      actions: [
        "Controleer accounts waar je dezelfde gebruikersnaam gebruikt.",
        "Gebruik waar mogelijk unieke accountnamen op gevoelige diensten.",
        "Blijf alert op berichten die specifiek naar jouw accountnaam verwijzen."
      ],
      severity_code: "low"
    },
    {
      key: "identity",
      match: /(naam|geboorte|birth|adres|postcode|woonplaats|persoons|identity|identiteit)/i,
      title: "Persoonsgegevens",
      meaning: "Persoonsgegevens kunnen worden gebruikt om profielen op te bouwen of om frauduleuze berichten geloofwaardiger te maken.",
      actions: [
        "Wees extra alert op verzoeken om aanvullende gegevens of verificatie.",
        "Controleer of gevoelige accounts extra beveiligd zijn.",
        "Meld mogelijke identiteitsfraude bij de juiste instanties als daar aanleiding voor is."
      ],
      severity_code: "medium"
    },
    {
      key: "device",
      match: /(ip|ip-adres|device|apparaat|browser|sessie|session|login|inlogpoging)/i,
      title: "Apparaat- of sessiegegevens",
      meaning: "Technische account- of sessiegegevens zijn meestal minder gevoelig, maar kunnen samen met andere data wel bruikbaarder worden.",
      actions: [
        "Controleer recente sessies of apparaten bij belangrijke accounts.",
        "Log uit op apparaten die je niet vertrouwt.",
        "Verander je wachtwoord als je onverwachte sessies ziet."
      ],
      severity_code: "low"
    }
  ];

  function normalizePlaybookItem(item) {
    if (typeof item === "string") {
      return {
        title: item.trim(),
        meaning: "",
        actions: [],
        severity_code: "medium",
        cta_label: "",
        cta_url: ""
      };
    }

    if (!isObj(item)) return null;

    var title = firstNonEmpty(item.title_nl, item.title, item.name_nl, item.name, item.category_nl, item.category);
    if (!title) return null;

    return {
      title: title,
      meaning: firstNonEmpty(item.meaning_nl, item.meaning, item.description_nl, item.description),
      actions: uniqueStrings(item.actions_nl || item.actions || item.advice_nl || item.advice || item.steps_nl || item.steps || []),
      severity_code: normalizeSeverityCode(firstNonEmpty(item.severity_code, item.severity)) || "medium",
      cta_label: firstNonEmpty(item.cta_label_nl, item.cta_label, item.cta_text_nl, item.cta_text),
      cta_url: firstNonEmpty(item.cta_url, item.url)
    };
  }

  function derivePlaybookFromLabels(labels) {
    var items = [];
    var seen = Object.create(null);

    uniqueStrings(labels).forEach(function (label) {
      for (var i = 0; i < PLAYBOOK_LIBRARY.length; i++) {
        var def = PLAYBOOK_LIBRARY[i];
        if (!def.match.test(label)) continue;
        if (seen[def.key]) break;
        seen[def.key] = true;
        items.push({
          title: def.title,
          meaning: def.meaning,
          actions: def.actions.slice(),
          severity_code: def.severity_code,
          cta_label: "",
          cta_url: ""
        });
        break;
      }
    });

    return items;
  }

  function derivePlaybookItems(candidate, breaches, totalMatchCount, genericFindings) {
    var raw =
      candidate.playbook_items_nl ||
      candidate.playbook_items ||
      candidate.playbook_nl ||
      candidate.playbook ||
      candidate.category_playbook_nl ||
      candidate.category_playbook ||
      candidate.guidance_cards_nl ||
      candidate.guidance_cards ||
      null;

    if (typeof raw === "string") {
      var parsed = safeParseAnyJsonString(raw);
      if (parsed != null) raw = parsed;
    }

    if (isObj(raw) && !Array.isArray(raw)) {
      raw = Object.keys(raw).map(function (k) {
        return raw[k];
      });
    }

    var items = [];
    if (Array.isArray(raw)) {
      raw.forEach(function (entry) {
        var item = normalizePlaybookItem(entry);
        if (item) items.push(item);
      });
    }

    if (!items.length) {
      var labels = [];
      (Array.isArray(breaches) ? breaches : []).forEach(function (b) {
        labels = labels.concat(toStringArray(b.leaked_data_nl));
      });

      items = derivePlaybookFromLabels(labels);
    }

    if (!items.length && Number(totalMatchCount || 0) > 0) {
      items.push({
        title: "Accountbeveiliging",
        meaning: "Er zijn bekende signalen gevonden voor deze controle. Begin bij je e-mail en belangrijkste accounts en zet daarna monitoring aan voor nieuwe signalen.",
        actions: [
          "Verander wachtwoorden die je hergebruikt.",
          "Zet 2FA aan op e-mail en andere belangrijke accounts.",
          "Controleer verdachte links altijd eerst apart via de link-checker."
        ],
        severity_code: "medium",
        cta_label: "",
        cta_url: ""
      });
    }

    if (!items.length && toStringArray(genericFindings).length) {
      items.push({
        title: "Voorzorg en monitoring",
        meaning: "Vandaag zijn er geen uitgewerkte detailkaarten nodig, maar het blijft verstandig om nieuwe signalen automatisch te laten volgen.",
        actions: [
          "Gebruik unieke wachtwoorden per account.",
          "Zet 2FA aan waar mogelijk.",
          "Laat monitoring nieuwe signalen automatisch voor je bewaken."
        ],
        severity_code: "low",
        cta_label: "",
        cta_url: ""
      });
    }

    var seenTitle = Object.create(null);
    return items.filter(function (item) {
      var key = String(item.title || "").trim().toLowerCase();
      if (!key) return false;
      if (seenTitle[key]) return false;
      seenTitle[key] = true;
      return true;
    });
  }

  function deriveGenericFindings(candidate, emailCount, phoneCount, usernameCount, phoneRaw, usernameRaw) {
    var findings = [];

    var explicit =
      candidate.generic_findings_nl ||
      candidate.findings_nl ||
      candidate.extra_findings_nl ||
      candidate.findings ||
      null;

    if (typeof explicit === "string") {
      var parsed = safeParseAnyJsonString(explicit);
      if (parsed != null) explicit = parsed;
    }

    findings = findings.concat(toStringArray(explicit));

    if (Number(emailCount || 0) > 0 && !findings.length) {
      findings.push("Er zijn bekende matches gevonden in de gecontroleerde bronnen, maar de bron leverde geen losse detailkaarten mee. Gebruik het actiecentrum voor de eerste vervolgstappen.");
    }

    if (Number(phoneCount || 0) > 0) {
      findings.push(
        "Voor het gecontroleerde telefoonnummer zijn " +
          String(phoneCount) +
          " bekende signaal" +
          (Number(phoneCount) === 1 ? "" : "en") +
          " gevonden in de gecontroleerde bronnen."
      );
    }

    if (Number(usernameCount || 0) > 0) {
      findings.push(
        "Voor de gecontroleerde gebruikersnaam zijn " +
          String(usernameCount) +
          " bekende signaal" +
          (Number(usernameCount) === 1 ? "" : "en") +
          " gevonden in de gecontroleerde bronnen."
      );
    }

    if (!findings.length && (phoneRaw || usernameRaw) && (Number(phoneCount || 0) > 0 || Number(usernameCount || 0) > 0)) {
      findings.push("Er zijn bekende signalen gevonden voor één of meer extra gecontroleerde gegevens.");
    }

    return uniqueStrings(findings);
  }

  // ------------------------------------------------------------
  // SESSION RUNTIME STATE (ACK / META / LOCK / CACHE)
  // ------------------------------------------------------------
  function readSessionMeta(sessionId) {
    var raw = tryLocalStorageGet(storageKey(STORAGE.sessionMetaPrefix, sessionId));
    var parsed = safeJSONParse(raw, null);
    return isObj(parsed) ? parsed : null;
  }

  function writeSessionMeta(sessionId, patch) {
    var prev = readSessionMeta(sessionId) || {};
    var next = Object.assign({}, prev, patch || {}, { _updated_at: now() });
    tryLocalStorageSet(storageKey(STORAGE.sessionMetaPrefix, sessionId), JSON.stringify(next));
    return next;
  }

  function readTriggerAck(sessionId) {
    var raw = tryLocalStorageGet(storageKey(STORAGE.sessionAckPrefix, sessionId));
    var parsed = safeJSONParse(raw, null);
    return isObj(parsed) ? parsed : null;
  }

  function writeTriggerAck(sessionId, patch) {
    var prev = readTriggerAck(sessionId) || {};
    var next = Object.assign({}, prev, patch || {});
    if (!isFinite(Number(next.ts))) next.ts = now();
    next.ts = Number(next.ts) || now();
    tryLocalStorageSet(storageKey(STORAGE.sessionAckPrefix, sessionId), JSON.stringify(next));
    return next;
  }

  function clearTriggerAck(sessionId) {
    tryLocalStorageRemove(storageKey(STORAGE.sessionAckPrefix, sessionId));
  }

  function triggerAckIsFresh(ack) {
    if (!ack || !isObj(ack)) return false;
    var ts = Number(ack.ts);
    if (!isFinite(ts) || ts <= 0) return false;
    return now() - ts < TIMING.TRIGGER_ACK_TTL_MS;
  }

  // ------------------------------------------------------------
  // CONFIG + DOM RESOLUTION
  // ------------------------------------------------------------
  function parseConfig(root) {
    var cfgEl = qs(root, "script[data-ids-qs-config]");
    if (!cfgEl) return {};
    try {
      return JSON.parse(cfgEl.textContent || "{}") || {};
    } catch (e) {
      return {};
    }
  }

  function resolveEl(root, cfg, selectorKey, fallbackSelector) {
    var id = cfg && cfg.selectors ? cfg.selectors[selectorKey] : "";
    var el = id ? byId(id) : null;
    if (el) return el;
    if (fallbackSelector) return qs(root, fallbackSelector);
    return null;
  }

  function resolveUI(root, cfg) {
    var ui = {
      root: root,

      // Landing form
      form: resolveEl(root, cfg, "form_id", "[data-ids-qs-form]"),
      emailInput: resolveEl(root, cfg, "email_input", 'input[name="input_email"], [data-ids-qs-email]'),

      // Optional extras
      phoneInput: resolveEl(root, cfg, "phone_input", 'input[name="input_phone"], [data-ids-qs-phone]'),
      usernameInput: resolveEl(root, cfg, "username_input", 'input[name="input_username"], [data-ids-qs-username]'),

      ownCb: resolveEl(root, cfg, "own_id", 'input[name="own_email"]'),
      consentCb: resolveEl(root, cfg, "consent_id", 'input[name="consent"]'),
      consentBool: resolveEl(root, cfg, "consent_bool_id", 'input[name="consent_bool"]'),
      errEmail: resolveEl(root, cfg, "email_error_id", ".ids-qs__error[id*='err-email']"),
      errOwn: resolveEl(root, cfg, "own_error_id", ".ids-qs__error[id*='err-own']"),
      errConsent: resolveEl(root, cfg, "consent_error_id", ".ids-qs__error[id*='err-consent']"),
      status: resolveEl(root, cfg, "status_id", "[data-qs-status]"),
      submitBtn: qs(root, "[data-qs-cta], [data-ids-qs-cta]"),

      // Confirm modal
      confirmOverlay: resolveEl(root, cfg, "confirm_overlay_id", "[data-ids-qs-confirm-overlay], [data-qs-confirm-overlay]"),
      confirmEmail: resolveEl(root, cfg, "confirm_email_id", "[data-ids-confirm-email], [data-qs-confirm-email]"),
      confirmPrice: resolveEl(root, cfg, "confirm_price_id", "[data-ids-confirm-price], [data-qs-confirm-price]"),
      confirmPhone: resolveEl(root, cfg, "confirm_phone_id", "[data-ids-confirm-phone], [data-qs-confirm-phone]"),
      confirmUsername: resolveEl(root, cfg, "confirm_username_id", "[data-ids-confirm-username], [data-qs-confirm-username]"),
      confirmExtraHost: resolveEl(root, cfg, "confirm_extra_host_id", "[data-ids-confirm-extra]"),

      // Result page root / shells
      reportRoot: resolveEl(root, cfg, "report_root_id", "[data-qs-report], [data-ids-qs-report-root]"),
      loadingShell: resolveEl(root, cfg, "loading_shell_id", "[data-qs-loading], [data-qs-loader]"),
      errorShell: resolveEl(root, cfg, "error_shell_id", "[data-qs-error]"),
      reportShell: resolveEl(root, cfg, "report_shell_id", "[data-qs-report]"),

      // Optional page-level outcome hooks
      pageTitle: resolveEl(root, cfg, "page_title_id", "[data-qs-page-title]"),
      pageSubtitle: resolveEl(root, cfg, "page_subtitle_id", "[data-qs-page-subtitle]"),

      // Status / loader / progress
      reportStatusText: resolveEl(root, cfg, "report_status_text_id", "[data-qs-status-text]"),
      reportStatusTextAll: qsa(root, "[data-qs-status-text]"),
      loaderWrap: resolveEl(root, cfg, "loader_wrap_id", "[data-qs-loader], [data-qs-loading]"),
      loaderLine1: resolveEl(root, cfg, "loader_line1_id", "[data-qs-loader-line1]"),
      loaderLine2: resolveEl(root, cfg, "loader_line2_id", "[data-qs-loader-line2]"),
      progressBar: resolveEl(root, cfg, "progress_bar_id", "[data-qs-progress-bar]"),
      progressTrack: qs(root, "[role='progressbar']"),
      progressText: resolveEl(root, cfg, "progress_text_id", "[data-qs-progress-text]"),

      // Timeline / steps
      reportTimeline: resolveEl(root, cfg, "report_status_id", "[data-qs-timeline]"),

      // Report content hooks
      reportHeadline: resolveEl(root, cfg, "report_headline_id", "[data-qs-report-headline], [data-qs-headline]"),
      reportSummary: resolveEl(root, cfg, "report_summary_id", "[data-qs-report-summary]"),
      reportWhyNow: resolveEl(root, cfg, "report_why_now_id", "[data-qs-report-why-now]"),
      reportActions: resolveEl(root, cfg, "report_actions_id", "[data-qs-report-actions]"),
      reportLimitations: resolveEl(root, cfg, "report_limitations_id", "[data-qs-report-limitations]"),
      reportBreaches: resolveEl(root, cfg, "report_breaches_id", "[data-qs-report-breaches]"),

      // Legacy hosts / templates
      summaryHost: qs(root, "[data-qs-summary-host]"),
      nomatchHost: qs(root, "[data-qs-nomatch-host]"),
      nomatchTemplate: qs(root, "template[data-qs-nomatch-template]"),
      breachTemplate: qs(root, "template[data-qs-breach-item]"),

      // Playbook
      playbookWrap: qs(root, "[data-qs-playbook]"),
      playbookHost: qs(root, "[data-qs-playbook-host]"),
      playbookTemplate: qs(root, "template[data-qs-playbook-item]"),

      // Meta / score / severity
      reportSeverity: resolveEl(root, cfg, "report_sev_id", "[data-qs-report-severity], [data-qs-sev]"),
      scoreEl: qs(root, "[data-qs-report-score], [data-qs-score]"),
      scoreBar: qs(root, "[data-qs-score-bar]"),
      scoreNote: qs(root, "[data-qs-score-note]"),
      scoreLabel: qs(root, "[data-qs-score-label]"),
      severityLabel: qs(root, "[data-qs-severity-label]"),
      reportEmail: qs(root, "[data-qs-report-email], [data-qs-meta-email]"),
      reportCount: qs(root, "[data-qs-report-count], [data-qs-meta-count]"),
      reportLastSeen: qs(root, "[data-qs-report-last-seen], [data-qs-meta-lastseen]"),
      reportPhoneWrap: qs(root, "[data-qs-report-phone-wrap]"),
      reportPhone: qs(root, "[data-qs-report-phone], [data-qs-meta-phone]"),
      reportUsernameWrap: qs(root, "[data-qs-report-username-wrap]"),
      reportUsername: qs(root, "[data-qs-report-username], [data-qs-meta-username]"),

      // Optional extra blocks
      breachesWrap: qs(root, "[data-qs-breaches-wrap]"),
      breachesTitle: qs(root, "[data-qs-breaches-title]"),
      genericBlock: qs(root, "[data-qs-generic-block]"),
      offerBlock: qs(root, "[data-qs-offer]"),
      offerCode: qs(root, "[data-qs-offer-code]"),
      offerCopyBtn: qs(root, "[data-qs-offer-copy]"),
      retryHint: qs(root, "[data-qs-retry-hint]"),
      successOfferModal: qs(root, 'dialog[id$="-modal-success-offer"]'),

      // Actions
      retryBtn: resolveEl(root, cfg, "report_retry_btn_id", "[data-qs-retry]"),
      pdfBtn: resolveEl(root, cfg, "report_pdf_btn_id", "[data-qs-download-pdf]")
    };

    ui.breachesCard =
      ui.reportBreaches && ui.reportBreaches.closest
        ? ui.reportBreaches.closest(".ids-qsr__card")
        : null;

    return ui;
  }

  // ------------------------------------------------------------
  // RESPONSE NORMALIZATION (Make wrapper -> normalized meta/report)
  // ------------------------------------------------------------
  function looksLikeReportObject(obj) {
    if (!isObj(obj)) return false;
    if (Array.isArray(obj.breaches_human)) return true;
    if (typeof obj.headline_nl === "string") return true;
    if (typeof obj.overall_summary_nl === "string") return true;
    if (typeof obj.report_version === "string") return true;
    if (typeof obj.risk_score !== "undefined" && typeof obj.severity_code !== "undefined") return true;
    return false;
  }

  function deepFindReport(candidate, depth) {
    depth = depth || 0;
    if (depth > 8 || candidate == null) return null;

    if (looksLikeReportObject(candidate)) return candidate;

    if (typeof candidate === "string") {
      var parsed = safeParseAnyJsonString(candidate);
      if (parsed != null) return deepFindReport(parsed, depth + 1);
      return null;
    }

    if (Array.isArray(candidate)) {
      for (var i = 0; i < candidate.length; i++) {
        var foundArr = deepFindReport(candidate[i], depth + 1);
        if (foundArr) return foundArr;
      }
      return null;
    }

    if (isObj(candidate)) {
      var priorityKeys = [
        "result",
        "report",
        "data",
        "payload",
        "output",
        "body",
        "response",
        "json",
        "content",
        "message",
        "Result",
        "Bundle",
        "bundle",
        "choices"
      ];

      for (var p = 0; p < priorityKeys.length; p++) {
        var key = priorityKeys[p];
        if (candidate[key] != null) {
          var found1 = deepFindReport(candidate[key], depth + 1);
          if (found1) return found1;
        }
      }

      var keys = Object.keys(candidate);
      for (var j = 0; j < keys.length; j++) {
        var k = keys[j];
        var v = candidate[k];

        if (typeof v === "string") {
          var lower = String(k).toLowerCase();
          if (lower.indexOf("result") >= 0 || lower.indexOf("json") >= 0 || lower.indexOf("string") >= 0) {
            var found2 = deepFindReport(v, depth + 1);
            if (found2) return found2;
          }
        }

        var found3 = deepFindReport(v, depth + 1);
        if (found3) return found3;
      }
    }

    return null;
  }

  function findExplicitReportInPayload(root) {
    if (!isObj(root)) return null;

    if (looksLikeReportObject(root)) return root;
    if (looksLikeReportObject(root.result)) return root.result;
    if (looksLikeReportObject(root.report)) return root.report;

    if (isObj(root.data)) {
      if (looksLikeReportObject(root.data.result)) return root.data.result;
      if (looksLikeReportObject(root.data.report)) return root.data.report;
      if (typeof root.data === "string") {
        var parsedData = safeParseAnyJsonString(root.data);
        if (isObj(parsedData)) {
          if (looksLikeReportObject(parsedData.result)) return parsedData.result;
          if (looksLikeReportObject(parsedData.report)) return parsedData.report;
          if (looksLikeReportObject(parsedData)) return parsedData;
        }
      }
    }

    if (isObj(root.payload)) {
      if (looksLikeReportObject(root.payload.result)) return root.payload.result;
      if (looksLikeReportObject(root.payload.report)) return root.payload.report;
    }

    var candidateStringKeys = ["result", "report", "body", "response", "json", "data", "payload", "message", "content"];
    for (var i = 0; i < candidateStringKeys.length; i++) {
      var key2 = candidateStringKeys[i];
      var val = root[key2];
      if (typeof val !== "string") continue;

      var parsed = safeParseAnyJsonString(val);
      if (!parsed) continue;

      if (looksLikeReportObject(parsed)) return parsed;
      if (isObj(parsed) && looksLikeReportObject(parsed.result)) return parsed.result;
      if (isObj(parsed) && looksLikeReportObject(parsed.report)) return parsed.report;
    }

    return deepFindReport(root, 0);
  }

  function normalizeBreachItem(b) {
    b = isObj(b) ? b : {};
    return {
      name: String(b.name || b.breach_name || "Onbekend lek"),
      date: String(b.date || b.breach_date || ""),
      age_label_nl: String(b.age_label_nl || b.age_label || ""),
      leaked_data_nl: toStringArray(b.leaked_data_nl || b.leaked_data || b.compromised_data),
      what_happened_nl: String(b.what_happened_nl || b.summary_nl || b.what_happened || ""),
      what_criminals_can_do_nl: toStringArray(b.what_criminals_can_do_nl || b.criminal_risks_nl || b.criminals_can_do),
      advice_now_nl: toStringArray(
        b.advice_now_nl ||
          b.advice ||
          b.next_steps_nl ||
          b.what_to_do_nl ||
          b.what_to_do ||
          ""
      ),
      source_url: String(b.source_url || b.source || "")
    };
  }

  function severityHeuristicFromCount(count) {
    count = Number(count);
    if (!isFinite(count) || count <= 0) return "clear";
    if (count >= 5) return "high";
    if (count >= 2) return "warning";
    return "medium";
  }

  function normalizeSeverityCode(value) {
    var s = String(value || "").toLowerCase().trim();
    if (!s) return "";
    if (s === "ok") return "clear";
    if (s === "none") return "clear";
    if (s === "warn") return "warning";
    return s;
  }

  function severityRank(code) {
    switch (normalizeSeverityCode(code)) {
      case "critical":
        return 5;
      case "high":
        return 4;
      case "warning":
        return 3;
      case "medium":
        return 2;
      case "low":
        return 1;
      case "clear":
        return 0;
      default:
        return -1;
    }
  }

  function pickMostSevereCode(a, b, fallback) {
    var sa = normalizeSeverityCode(a);
    var sb = normalizeSeverityCode(b);

    if (!sa && !sb) return normalizeSeverityCode(fallback || "") || "";
    if (!sa) return sb;
    if (!sb) return sa;

    return severityRank(sa) >= severityRank(sb) ? sa : sb;
  }

  function normalizeMakePayload(raw, fallbackEmail, opts) {
    opts = opts || {};
    var root = isObj(raw) ? raw : {};
    var explicitReport = opts.explicitReport || null;
    var explicitReportFound = !!explicitReport;

    var candidate =
      root.result != null
        ? root.result
        : root.report != null
          ? root.report
          : isObj(root.data) && root.data.result != null
            ? root.data.result
            : isObj(root.data) && root.data.report != null
              ? root.data.report
              : isObj(root.payload) && root.payload.result != null
                ? root.payload.result
                : isObj(root.payload) && root.payload.report != null
                  ? root.payload.report
                  : root;

    if (explicitReportFound) candidate = explicitReport;

    if (typeof candidate === "string") {
      var parsedCandidate = safeParseAnyJsonString(candidate);
      if (parsedCandidate != null) candidate = parsedCandidate;
    }

    if (!looksLikeReportObject(candidate)) {
      var deepFound = explicitReportFound ? explicitReport : deepFindReport(root, 0);
      if (deepFound) candidate = deepFound;
    }

    if (!isObj(candidate)) candidate = {};

    // -------------------------
    // Breaches array
    // -------------------------
    var breachesRaw = candidate.breaches_human;
    if (typeof breachesRaw === "string") {
      var parsedBreaches = safeParseAnyJsonString(breachesRaw);
      if (Array.isArray(parsedBreaches)) breachesRaw = parsedBreaches;
    }
    if (!Array.isArray(breachesRaw)) breachesRaw = [];
    var breaches = breachesRaw.map(normalizeBreachItem);

    // -------------------------
    // Counts
    // -------------------------
    var emailCountFromBreaches = breaches.length;
    var emailCountFromRoot = Number(root.breach_count);
    var emailCountFromCandidate = Number(candidate.breach_count);

    var emailMatchCount = isFinite(emailCountFromRoot)
      ? emailCountFromRoot
      : isFinite(emailCountFromCandidate)
        ? emailCountFromCandidate
        : emailCountFromBreaches;

    if (!isFinite(emailMatchCount)) emailMatchCount = emailCountFromBreaches;

    var phoneCountHint = Number(root.phone_breach_count || candidate.phone_breach_count || 0);
    if (!isFinite(phoneCountHint) || phoneCountHint < 0) phoneCountHint = 0;

    var usernameCountHint = Number(root.username_breach_count || candidate.username_breach_count || 0);
    if (!isFinite(usernameCountHint) || usernameCountHint < 0) usernameCountHint = 0;

    var totalMatchCount = Math.max(
      0,
      Number(emailMatchCount) || 0,
      Number(phoneCountHint) || 0,
      Number(usernameCountHint) || 0,
      Number(emailCountFromBreaches) || 0
    );

    // -------------------------
    // Severity
    // -------------------------
    var severity = pickMostSevereCode(
      root.severity_code,
      candidate.severity_code,
      severityHeuristicFromCount(totalMatchCount)
    );
    if (!severity) severity = severityHeuristicFromCount(totalMatchCount);

    if (breaches.length === 0 && totalMatchCount === 0) {
      severity = "clear";
    }

    // -------------------------
    // Risk score
    // -------------------------
    var riskScore = Number(candidate.risk_score);
    if (!isFinite(riskScore)) {
      if (severity === "critical") riskScore = 90;
      else if (severity === "high") riskScore = 75;
      else if (severity === "warning" || severity === "medium") riskScore = 50;
      else if (severity === "low") riskScore = 20;
      else riskScore = totalMatchCount ? 35 : 5;
    }
    riskScore = clamp(riskScore, 0, 100);

    // -------------------------
    // Latest seen
    // -------------------------
    var breachDates = breaches
      .map(function (b) {
        return b.date;
      })
      .filter(Boolean);

    var latest = latestDate(
      breachDates.concat(String(root.last_seen_at || "") ? [String(root.last_seen_at)] : [])
    );

    // -------------------------
    // Phone / username extraction
    // -------------------------
    var phoneRaw = String(
      root.input_phone ||
        root.phone_e164 ||
        root.phone ||
        candidate.input_phone ||
        candidate.phone_e164 ||
        candidate.phone ||
        ""
    ).trim();

    if (phoneRaw) phoneRaw = phoneRaw.replace(/^"+|"+$/g, "").trim();
    if (!phoneRaw) {
      phoneRaw = String(tryLocalStorageGet(STORAGE.phone) || "").trim();
      if (phoneRaw) phoneRaw = phoneRaw.replace(/^"+|"+$/g, "").trim();
    }

    var usernameRaw = String(
      root.input_username ||
        root.username ||
        root.handle ||
        candidate.input_username ||
        candidate.username ||
        candidate.handle ||
        ""
    ).trim();

    if (usernameRaw) usernameRaw = usernameRaw.replace(/^"+|"+$/g, "").trim();
    if (!usernameRaw) {
      usernameRaw = String(tryLocalStorageGet(STORAGE.username) || "").trim();
      if (usernameRaw) usernameRaw = usernameRaw.replace(/^"+|"+$/g, "").trim();
    }

    var phoneMasked = phoneRaw ? maskPhone(phoneRaw) : "";
    var usernameMasked = usernameRaw ? maskUsername(usernameRaw) : "";

    var phoneStatusRaw = String(root.phone_status || candidate.phone_status || "").toLowerCase().trim();
    var phoneStatus = "";
    if (phoneStatusRaw === "clean" || phoneStatusRaw === "clear" || phoneStatusRaw === "ok") phoneStatus = "clean";
    else if (phoneStatusRaw) phoneStatus = phoneStatusRaw;
    else if (phoneRaw) phoneStatus = phoneCountHint > 0 ? "breached" : "clean";

    var usernameStatusRaw = String(root.username_status || candidate.username_status || "").toLowerCase().trim();
    var usernameStatus = "";
    if (usernameStatusRaw === "clean" || usernameStatusRaw === "clear" || usernameStatusRaw === "ok") usernameStatus = "clean";
    else if (usernameStatusRaw) usernameStatus = usernameStatusRaw;
    else if (usernameRaw) usernameStatus = usernameCountHint > 0 ? "breached" : "clean";

    // -------------------------
    // Generic findings + playbook
    // -------------------------
    var genericFindings = deriveGenericFindings(
      candidate,
      emailMatchCount,
      phoneCountHint,
      usernameCountHint,
      phoneRaw,
      usernameRaw
    );

    var playbookItems = derivePlaybookItems(candidate, breaches, totalMatchCount, genericFindings);

    // -------------------------
    // Copy
    // -------------------------
    var headline = String(candidate.headline_nl || "");
    if (!headline) {
      if (totalMatchCount === 0) headline = "Goed nieuws: geen bekende matches gevonden";
      else if (breaches.length > 0) headline = "We hebben bekende matches gevonden";
      else headline = "We hebben bekende signalen gevonden";
    }

    var summary = String(candidate.overall_summary_nl || "");
    if (!summary) {
      if (totalMatchCount === 0) {
        summary = "In de gecontroleerde bronnen is voor deze controle op dit moment geen bekende match gevonden. Dit is een momentopname.";
      } else if (breaches.length > 0) {
        summary = "Er zijn bekende matches gevonden in gecontroleerde datalekbronnen. Bekijk hieronder wat dit betekent en welke stap nu het meest logisch is.";
      } else {
        summary = "Er zijn bekende signalen gevonden voor één of meer gecontroleerde gegevens. Gebruik het actiecentrum voor de eerste vervolgstappen.";
      }
    }

    var whyNow = String(candidate.why_now_nl || "");
    if (!whyNow) {
      if (totalMatchCount === 0) {
        whyNow = "Geen bekende match betekent niet dat risico nul is — wel dat deze scan nu geen bekende vermelding heeft gevonden in de gecontroleerde bronnen.";
      } else {
        whyNow = "Begin bij je e-mail, wachtwoorden en 2FA op je belangrijkste accounts. Zet daarna monitoring aan voor nieuwe signalen.";
      }
    }

    var actions = toStringArray(candidate.key_action_today_nl);
    var limitations = toStringArray(candidate.limitations_nl);

    var pdfUrl = String(candidate.pdf_url || candidate.report_pdf_url || candidate.last_report_url || candidate.download_url || "");

    var report = {
      report_version: String(candidate.report_version || "2.0"),
      email_masked: String(candidate.email_masked || root.email_masked || (fallbackEmail ? maskEmail(fallbackEmail) : "")),
      severity_code: severity,
      risk_score: riskScore,
      headline_nl: headline,
      overall_summary_nl: summary,
      why_now_nl: whyNow,
      key_action_today_nl: actions,
      breaches_human: breaches,
      limitations_nl: limitations,
      latest_breach_date: latest ? latest.toISOString().slice(0, 10) : "",
      latest_breach_date_label: latest ? formatDateNL(latest.toISOString()) : "—",
      pdf_url: validHttpUrl(pdfUrl) ? pdfUrl : "",
      total_match_count: totalMatchCount,
      email_match_count: emailMatchCount,
      phone_match_count: phoneCountHint,
      username_match_count: usernameCountHint,
      playbook_items_nl: playbookItems,
      generic_findings_nl: genericFindings
    };

    var meta = {
      ok: root.ok === true || String(root.ok).toLowerCase() === "true",
      status: String(root.status || candidate.status || "").toLowerCase().trim(),
      job_id: String(root.job_id || root.worker_req_id || candidate.job_id || ""),
      ctx_channel: String(root.ctx_channel || candidate.ctx_channel || ""),
      email_masked: String(root.email_masked || report.email_masked || ""),
      severity_code: String(root.severity_code || report.severity_code || ""),
      breach_count: totalMatchCount,
      total_match_count: totalMatchCount,
      email_breach_count: emailMatchCount,
      last_seen_at: String(root.last_seen_at || report.latest_breach_date || ""),
      _explicit_report: explicitReportFound,

      // phone
      phone_provided: phoneRaw ? "true" : "",
      phone_masked: phoneMasked,
      phone_status: phoneStatus,
      phone_breach_count: phoneCountHint,

      // username
      username_provided: usernameRaw ? "true" : "",
      username_masked: usernameMasked,
      username_status: usernameStatus,
      username_breach_count: usernameCountHint
    };

    return { raw: root, meta: meta, report: report };
  }

  function hasRenderableReport(normalized) {
    if (!normalized || !normalized.report || !normalized.meta) return false;

    var r = normalized.report;
    var m = normalized.meta;
    var status = String(m.status || "").toLowerCase();
    var explicit = m._explicit_report === true;

    if (!explicit) return false;

    var doneLike =
      status === "done" ||
      status === "completed" ||
      status === "complete" ||
      status === "success" ||
      status === "ok" ||
      status === "";

    if (!doneLike) return false;
    if (!Array.isArray(r.breaches_human)) return false;

    if (r.breaches_human.length === 0) {
      var hasText =
        !!String(r.headline_nl || "").trim() ||
        !!String(r.overall_summary_nl || "").trim();

      var total = Number(r.total_match_count || m.total_match_count || m.breach_count || 0);
      var hasGenericFindings = toStringArray(r.generic_findings_nl).length > 0;
      var hasPlaybook = Array.isArray(r.playbook_items_nl) && r.playbook_items_nl.length > 0;

      if (!hasText && !(isFinite(total) && total >= 0) && !hasGenericFindings && !hasPlaybook) return false;
    }

    return true;
  }

  function deepFindByKeys(candidate, keys, depth) {
    depth = depth || 0;
    if (depth > 6 || candidate == null) return null;

    if (isObj(candidate)) {
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (candidate[k] != null && candidate[k] !== "") return candidate[k];
      }

      var objKeys = Object.keys(candidate);
      for (i = 0; i < objKeys.length; i++) {
        var v = candidate[objKeys[i]];
        var found = deepFindByKeys(v, keys, depth + 1);
        if (found != null && found !== "") return found;
      }
    } else if (Array.isArray(candidate)) {
      for (var j = 0; j < candidate.length; j++) {
        var f = deepFindByKeys(candidate[j], keys, depth + 1);
        if (f != null && f !== "") return f;
      }
    } else if (typeof candidate === "string") {
      var parsed = safeParseAnyJsonString(candidate);
      if (parsed != null) return deepFindByKeys(parsed, keys, depth + 1);
    }

    return null;
  }

  function extractNormalizedFromResponseEnvelope(envelope, fallbackEmail) {
    var candidate = envelope;

    if (isObj(envelope) && ("json" in envelope || "text" in envelope)) {
      candidate = envelope.json != null ? envelope.json : envelope.text;
    }

    if (typeof candidate === "string") {
      candidate = safeParseAnyJsonString(candidate) || {};
    }

    if (!isObj(candidate)) return null;

    var explicitReport = findExplicitReportInPayload(candidate);

    if (!explicitReport) {
      var statusRaw = deepFindByKeys(candidate, ["status", "state"], 0);
      var status = String(statusRaw || "").toLowerCase().trim();

      if (isDoneLikeStatus(status)) {
        var rj = deepFindByKeys(candidate, ["result_json"], 0);
        if (typeof rj === "string" && rj.trim()) {
          var parsedRj = safeParseAnyJsonString(rj);
          if (parsedRj != null) {
            var found = deepFindReport(parsedRj, 0) || (looksLikeReportObject(parsedRj) ? parsedRj : null);
            if (!found && isObj(parsedRj)) {
              if (looksLikeReportObject(parsedRj.result)) found = parsedRj.result;
              else if (looksLikeReportObject(parsedRj.report)) found = parsedRj.report;
            }
            if (found) explicitReport = found;
          }
        }
      }
    }

    if (!explicitReport) return null;

    var normalized = normalizeMakePayload(candidate, fallbackEmail, { explicitReport: explicitReport });
    if (hasRenderableReport(normalized)) return normalized;

    return null;
  }

  // ------------------------------------------------------------
  // ACK HELPERS
  // ------------------------------------------------------------
  function envelopeToObject(envelope) {
    var candidate = envelope;
    if (isObj(envelope) && ("json" in envelope || "text" in envelope)) {
      candidate = envelope.json != null ? envelope.json : envelope.text;
    }
    if (typeof candidate === "string") {
      candidate = safeParseAnyJsonString(candidate) || {};
    }
    return isObj(candidate) ? candidate : null;
  }

  function extractServerAckInfo(envelope) {
    var obj = envelopeToObject(envelope);
    if (!obj) {
      return { raw: null, status: "", status_message: "", job_id: "", already_started: false, accepted: false };
    }

    var statusRaw = deepFindByKeys(obj, ["status", "state"], 0);
    var msgRaw = deepFindByKeys(obj, ["status_message", "message", "detail"], 0);
    var jobIdRaw = deepFindByKeys(obj, ["job_id", "worker_req_id"], 0);
    var alreadyStartedRaw = deepFindByKeys(obj, ["already_started", "deduped"], 0);
    var okRaw = deepFindByKeys(obj, ["ok", "accepted"], 0);

    var status = String(statusRaw || "").toLowerCase().trim();
    var statusMsg = String(msgRaw || "").trim();
    var jobId = String(jobIdRaw || "").trim();
    var alreadyStarted = asBool(alreadyStartedRaw);

    var accepted =
      asBool(okRaw) ||
      status === "accepted" ||
      status === "queued" ||
      status === "processing" ||
      status === "done" ||
      status === "success";

    return {
      raw: obj,
      status: status,
      status_message: statusMsg,
      job_id: jobId,
      already_started: alreadyStarted,
      accepted: accepted
    };
  }

  // ------------------------------------------------------------
  // SAFE FETCH
  // ------------------------------------------------------------
  function safeFetchJson(url, fetchOptions) {
    return fetch(url, fetchOptions)
      .then(function (res) {
        return res.text().then(function (bodyText) {
          var contentType = "";
          try {
            contentType = (res.headers && res.headers.get && res.headers.get("content-type")) || "";
          } catch (e) {}

          var parsed = safeJSONParse(bodyText, null);
          if (parsed == null) parsed = safeParseAnyJsonString(bodyText);

          return {
            ok: !!res.ok,
            status: Number(res.status || 0),
            statusText: res.statusText || "",
            contentType: contentType,
            text: bodyText,
            json: parsed
          };
        });
      })
      .catch(function (err) {
        return {
          ok: false,
          status: 0,
          statusText: "network_error",
          contentType: "",
          text: String((err && err.message) || err || "Network error"),
          json: null,
          _network_error: true
        };
      });
  }

  function postWebhook(primaryUrl, fallbackUrl, payload) {
    var url = String(primaryUrl || "").trim();
    var fallback = String(fallbackUrl || "").trim();

    var idem = String((payload && payload.idempotency_key) || "");
    var sess = String((payload && payload.session_id) || "");
    var mode = String((payload && payload.mode) || "");

    function doPost(u) {
      return safeFetchJson(u, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          Accept: "application/json, text/plain;q=0.9, */*;q=0.8",
          "X-Idempotency-Key": idem,
          "X-IdenScan-Source": "ids-quickscan-js",
          "X-IdenScan-Session": sess,
          "X-IdenScan-Mode": mode
        },
        body: toFormBody(payload),
        credentials: "omit",
        cache: "no-store",
        mode: "cors",
        redirect: "follow"
      });
    }

    return doPost(url).then(function (res) {
      if ((!res.ok && (res.status === 0 || res._network_error)) && fallback && fallback !== url) {
        pushDL("qs_webhook_fallback_try", { mode: mode, primary_is_make: isProbablyMakeWebhookUrl(url) });
        return doPost(fallback);
      }
      return res;
    });
  }

  // ------------------------------------------------------------
  // UI STATE + TIMELINE + PROGRESS
  // ------------------------------------------------------------
  function severityView(sev) {
    sev = String(sev || "").toLowerCase();
    switch (sev) {
      case "critical":
        return { label: "Kritiek", note: "Direct actie nemen vandaag." };
      case "high":
        return { label: "Hoog", note: "Actie aanbevolen op korte termijn." };
      case "warning":
      case "medium":
        return { label: "Waarschuwing", note: "Verlaag risico met de aanbevolen stappen." };
      case "low":
        return { label: "Laag", note: "Beperkte impact, blijf alert." };
      case "clear":
      case "none":
        return { label: "Geen bekende match", note: "Momentopname — geen garantie voor de toekomst." };
      default:
        return { label: "Onbekend", note: "Controle bezig of onvolledige data." };
    }
  }

  function setStatusTextAll(ui, value) {
    if (!ui) return;
    if (ui.reportStatusTextAll && ui.reportStatusTextAll.length) {
      ui.reportStatusTextAll.forEach(function (el) {
        text(el, value);
      });
      return;
    }
    if (ui.reportStatusText) text(ui.reportStatusText, value);
  }

  function setTimelineStep(ui, currentState) {
    var mapOrder = ["paid", "scanning", "processing", "done"];
    var idx = mapOrder.indexOf(currentState);
    qsa(ui.root, "[data-qs-step]").forEach(function (step) {
      var key = step.getAttribute("data-qs-step");
      var sIdx = mapOrder.indexOf(key);
      var state = "todo";

      if (idx >= 0) {
        if (sIdx < idx) state = "done";
        else if (sIdx === idx) state = "active";
        else state = "todo";
      }

      step.setAttribute("data-state", state);
    });
  }

  function setResultUiState(ui, state, opts) {
    opts = opts || {};
    var statusText = opts.statusText || "";
    var l1 = opts.loaderLine1 || "";
    var l2 = opts.loaderLine2 || "";
    var showRetryBtn = !!opts.showRetry;

    if (ui.root) ui.root.setAttribute("data-qs-state", state);

    if (ui.reportRoot) {
      show(ui.reportRoot);
      ui.reportRoot.setAttribute("data-qs-report-state", state);
    }

    if (statusText) setStatusTextAll(ui, statusText);

    if (ui.loadingShell) {
      if (state === QS_UI_STATES.DONE || state === QS_UI_STATES.ERROR) hide(ui.loadingShell);
      else show(ui.loadingShell);
    }

    if (ui.reportShell) {
      if (state === QS_UI_STATES.DONE) show(ui.reportShell);
      else if (ui.reportShell !== ui.reportRoot) hide(ui.reportShell);
    }

    if (ui.errorShell) {
      if (state === QS_UI_STATES.ERROR) show(ui.errorShell);
      else hide(ui.errorShell);
    }

    if (ui.loaderWrap) {
      if (state === QS_UI_STATES.DONE || state === QS_UI_STATES.ERROR) hide(ui.loaderWrap);
      else show(ui.loaderWrap);
    }

    if (ui.loaderLine1 && l1) text(ui.loaderLine1, l1);
    if (ui.loaderLine2 && l2) text(ui.loaderLine2, l2);

    if (ui.retryBtn) {
      if (showRetryBtn) show(ui.retryBtn);
      else hide(ui.retryBtn);
    }

    if (ui.retryHint) {
      if (state === QS_UI_STATES.SCANNING || state === QS_UI_STATES.PROCESSING) show(ui.retryHint);
      else hide(ui.retryHint);
    }

    if (state === QS_UI_STATES.PAID) setTimelineStep(ui, "paid");
    if (state === QS_UI_STATES.SCANNING) setTimelineStep(ui, "scanning");
    if (state === QS_UI_STATES.PROCESSING) setTimelineStep(ui, "processing");
    if (state === QS_UI_STATES.DONE) setTimelineStep(ui, "done");
    if (state === QS_UI_STATES.ERROR) setTimelineStep(ui, "processing");
  }

  function createProgressController(ui, maxMs) {
    maxMs = Number(maxMs || TIMING.PROGRESS_EXPECTED_MS);
    var bar = ui.progressBar;
    var track = ui.progressTrack || (bar && bar.parentElement ? bar.parentElement : null);
    var textEl = ui.progressText;
    var startTs = now();
    var rafId = null;
    var stopped = false;
    var lastSec = -1;

    var rotatingMessages = [
      "We controleren bekende datalekbronnen…",
      "We verwerken de resultaten…",
      "We bouwen je rapport in duidelijke taal…",
      "Bij grotere resultaten kan dit iets langer duren…"
    ];
    var currentMsgIndex = 0;
    var lastMsgTs = startTs;

    function setPercent(pct) {
      pct = clamp(pct, 0, 100);
      if (bar) {
        try {
          bar.style.width = pct + "%";
        } catch (e) {}
      }
      if (track) {
        try {
          track.setAttribute("aria-valuenow", String(Math.round(pct)));
        } catch (e2) {}
      }
    }

    function tick() {
      if (stopped) return;

      var t = now();
      var elapsed = t - startTs;

      var pct = Math.min(97, Math.round((elapsed / maxMs) * 97));
      setPercent(pct);

      if (textEl) {
        var secLeft = Math.max(0, Math.ceil((maxMs - elapsed) / 1000));
        if (secLeft !== lastSec) {
          lastSec = secLeft;
          if (secLeft > 0) text(textEl, "We bouwen je rapport… (" + secLeft + "s)");
          else text(textEl, "We ronden je rapport af…");
        }
      }

      var holdUntil = 0;
      try {
        holdUntil = Number(ui && ui.root && ui.root.dataset ? ui.root.dataset.qsStatusHoldUntil : 0);
      } catch (e3) {
        holdUntil = 0;
      }

      if (t - lastMsgTs >= TIMING.STATUS_ROTATE_MS && (!holdUntil || t >= holdUntil)) {
        currentMsgIndex = (currentMsgIndex + 1) % rotatingMessages.length;
        setStatusTextAll(ui, rotatingMessages[currentMsgIndex]);
        lastMsgTs = t;
      }

      rafId = window.requestAnimationFrame(tick);
    }

    function done(msg) {
      stopped = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      setPercent(100);
      if (textEl) text(textEl, msg || "Rapport gereed.");
    }

    function fail(msg) {
      stopped = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      if (textEl) text(textEl, msg || "Het laden van je rapport duurde te lang.");
    }

    tick();

    return { done: done, fail: fail };
  }

  // ------------------------------------------------------------
  // REPORT RENDERING
  // ------------------------------------------------------------
  function fillList(container, items) {
    if (!container) return;
    container.innerHTML = "";
    toStringArray(items).forEach(function (item) {
      var li = document.createElement("li");
      li.textContent = item;
      container.appendChild(li);
    });
  }

  function renderResultHero(ui, report) {
    if (!ui) return;

    var totalCount = Number(report.total_match_count || 0);
    var hasDetailedBreaches = Array.isArray(report.breaches_human) && report.breaches_human.length > 0;

    if (ui.root) {
      ui.root.setAttribute("data-qs-outcome", totalCount === 0 ? "clean" : "matches");
    }

    if (!ui.pageTitle && !ui.pageSubtitle) return;

    if (totalCount === 0) {
      if (ui.pageTitle) text(ui.pageTitle, "Goed nieuws: geen bekende matches gevonden");
      if (ui.pageSubtitle) {
        text(
          ui.pageSubtitle,
          "In de gecontroleerde bronnen is voor deze controle op dit moment geen bekende match gevonden. Wil je dat we dit automatisch blijven volgen? Zet dan monitoring aan."
        );
      }
      return;
    }

    if (hasDetailedBreaches) {
      if (ui.pageTitle) text(ui.pageTitle, "We hebben bekende matches gevonden");
      if (ui.pageSubtitle) {
        text(
          ui.pageSubtitle,
          "Bekijk hieronder wat er is gevonden en welke stap nu het meest logisch is. Een match betekent niet automatisch accountmisbruik, maar wel dat extra controle verstandig is."
        );
      }
      return;
    }

    if (ui.pageTitle) text(ui.pageTitle, "We hebben bekende signalen gevonden");
    if (ui.pageSubtitle) {
      text(
        ui.pageSubtitle,
        "Voor één of meer gecontroleerde gegevens zijn bekende signalen gevonden. Gebruik het actiecentrum en monitoring om de volgende stap rustig te nemen."
      );
    }
  }

  function renderSummary(ui, report) {
    if (ui.reportSummary) text(ui.reportSummary, report.overall_summary_nl || "");
    if (ui.reportWhyNow) text(ui.reportWhyNow, report.why_now_nl || "");

    if (!ui.summaryHost) return;
    ui.summaryHost.innerHTML = "";

    var p1 = document.createElement("p");
    p1.className = "ids-qs__summary-p";
    p1.textContent = report.overall_summary_nl || "";
    ui.summaryHost.appendChild(p1);

    if (report.why_now_nl) {
      var p2 = document.createElement("p");
      p2.className = "ids-qs__summary-p ids-qs__summary-p--muted";
      p2.textContent = report.why_now_nl;
      ui.summaryHost.appendChild(p2);
    }
  }

  function renderActions(ui, report) {
    var actions = toStringArray(report.key_action_today_nl);

    if (!actions.length) {
      if (Number(report.total_match_count || 0) > 0) {
        actions = [
          "Begin bij je e-mail en belangrijkste accounts: gebruik een uniek wachtwoord en zet 2FA aan.",
          "Vervang wachtwoorden die je hergebruikt op andere accounts.",
          "Open verdachte links liever via de officiële app of website zelf.",
          "Zet monitoring aan als je nieuwe signalen automatisch wilt laten volgen."
        ];
      } else {
        actions = [
          "Gebruik unieke wachtwoorden per dienst.",
          "Zet tweefactorauthenticatie (2FA) aan waar mogelijk.",
          "Blijf alert op phishing en verdachte links.",
          "Overweeg monitoring voor updates zodra er iets verandert."
        ];
      }
    }

    if (ui.reportActions) {
      var tag = ui.reportActions.tagName ? ui.reportActions.tagName.toLowerCase() : "";
      if (tag === "ul" || tag === "ol") fillList(ui.reportActions, actions);
      else html(ui.reportActions, makeListHtml(actions, "Geen acties beschikbaar."));
    }
  }

  function renderLimitations(ui, report) {
    var limits = toStringArray(report.limitations_nl);
    if (!limits.length) {
      limits = [
        "Dit is een momentopname op basis van bekende bronnen; nieuwe incidenten kunnen later alsnog zichtbaar worden.",
        "Geen bekende match betekent niet dat risico nul is — wel dat deze scan nu geen bekende vermelding heeft gevonden.",
        "Een match betekent niet automatisch accountmisbruik, maar wel dat extra controle verstandig is.",
        "Niet alle gelekte gegevens zijn altijd volledig of direct verifieerbaar."
      ];
    }

    if (ui.reportLimitations) {
      var tag = ui.reportLimitations.tagName ? ui.reportLimitations.tagName.toLowerCase() : "";
      if (tag === "ul" || tag === "ol") fillList(ui.reportLimitations, limits);
      else html(ui.reportLimitations, makeListHtml(limits, "Geen beperkingen beschikbaar."));
    }
  }

  function renderNoMatch(ui, report) {
    if (!ui.nomatchHost) return;

    var totalCount = Number(report.total_match_count || 0);
    var isNoMatch = totalCount === 0;

    ui.nomatchHost.innerHTML = "";

    if (!isNoMatch) {
      hide(ui.nomatchHost);
      if (ui.breachesCard) show(ui.breachesCard);
      return;
    }

    if (ui.breachesCard) hide(ui.breachesCard);

    if (ui.nomatchTemplate && "content" in ui.nomatchTemplate) {
      var frag = ui.nomatchTemplate.content.cloneNode(true);
      var actionList = qs(frag, "[data-qs-nomatch-actions]");
      if (actionList) {
        fillList(actionList, [
          "Sterke uitkomst: in de gecontroleerde bronnen is nu geen bekende match gevonden.",
          "Nieuwe incidenten kunnen later alsnog zichtbaar worden — monitoring voorkomt dat je zelf hoeft te blijven checken.",
          "Gebruik unieke wachtwoorden en 2FA op je belangrijkste accounts.",
          "Controleer verdachte links eerst via de link-checker."
        ]);
      }
      ui.nomatchHost.appendChild(frag);
    } else {
      var box = document.createElement("div");
      box.className = "ids-qsr__card";
      box.innerHTML =
        '<h3 class="ids-qsr__card-title">Goed nieuws: geen bekende matches gevonden</h3>' +
        '<p class="ids-qsr__muted">In de gecontroleerde bronnen is voor deze controle op dit moment geen bekende match gevonden. Dit blijft een momentopname.</p>';
      ui.nomatchHost.appendChild(box);
    }

    show(ui.nomatchHost);
  }

  function renderGenericFindingsCard(report) {
    var findings = toStringArray(report.generic_findings_nl);
    if (!findings.length) return null;

    var card = document.createElement("article");
    card.className = "ids-qsr__breach-card ids-qs__generic-findings";

    var block = document.createElement("div");
    block.className = "ids-qsr__block";

    var title = document.createElement("h5");
    title.textContent = "Bekende signalen gevonden";
    block.appendChild(title);

    var p = document.createElement("p");
    p.textContent = "Niet elke bron levert losse datalekkaarten aan. Hieronder zie je wat deze controle wel heeft teruggegeven.";
    block.appendChild(p);

    var ul = document.createElement("ul");
    findings.forEach(function (item) {
      var li = document.createElement("li");
      li.textContent = item;
      ul.appendChild(li);
    });
    block.appendChild(ul);

    card.appendChild(block);
    return card;
  }

  function renderBreachCardFromTemplate(ui, breach) {
    if (!(ui.breachTemplate && "content" in ui.breachTemplate)) return null;

    var frag = ui.breachTemplate.content.cloneNode(true);

    text(qs(frag, "[data-breach-name]"), breach.name || "Onbekend lek");
    text(qs(frag, "[data-breach-date]"), breach.age_label_nl || (breach.date ? formatDateNL(breach.date) : "Datum onbekend"));
    text(qs(frag, "[data-breach-summary]"), breach.what_happened_nl || "");

    var adviceTop = toStringArray(breach.advice_now_nl)[0];
    text(
      qs(frag, "[data-breach-what]"),
      adviceTop ||
        "Begin bij je e-mail en belangrijkste accounts: gebruik een uniek wachtwoord, zet 2FA aan en blijf alert op phishing."
    );

    var chipsWrap = qs(frag, "[data-breach-datachips]");
    if (chipsWrap) {
      chipsWrap.innerHTML = "";
      toStringArray(breach.leaked_data_nl).forEach(function (chip) {
        var span = document.createElement("span");
        span.className = "ids-qs__chip2";
        span.textContent = chip;
        chipsWrap.appendChild(span);
      });
    }

    var criminals = toStringArray(breach.what_criminals_can_do_nl);
    var criminalsWrap = qs(frag, "[data-breach-criminals-wrap]");
    var criminalsUl = qs(frag, "[data-breach-criminals]");
    if (criminals.length && criminalsWrap && criminalsUl) {
      fillList(criminalsUl, criminals);
      show(criminalsWrap);
    } else if (criminalsWrap) {
      hide(criminalsWrap);
    }

    var advice = toStringArray(breach.advice_now_nl);
    var adviceWrap = qs(frag, "[data-breach-advice-wrap]");
    var adviceUl = qs(frag, "[data-breach-advice]");
    if (advice.length && adviceWrap && adviceUl) {
      fillList(adviceUl, advice);
      show(adviceWrap);
    } else if (adviceWrap) {
      hide(adviceWrap);
    }

    var sourceLink = qs(frag, "[data-breach-source]");
    if (sourceLink) {
      if (validHttpUrl(breach.source_url)) {
        sourceLink.href = breach.source_url;
        show(sourceLink);
      } else {
        var wrap = sourceLink.closest ? sourceLink.closest(".ids-qs__breach-sourcewrap") : null;
        hide(wrap || sourceLink);
      }
    }

    return frag;
  }

  function renderBreaches(ui, report) {
    if (!ui.reportBreaches) return;

    ui.reportBreaches.innerHTML = "";

    var breaches = Array.isArray(report.breaches_human) ? report.breaches_human : [];
    var totalCount = Number(report.total_match_count || breaches.length || 0);

    if (ui.reportCount) text(ui.reportCount, String(totalCount));
    if (ui.breachesTitle) {
      text(
        ui.breachesTitle,
        totalCount === 0
          ? "Geen bekende matches gevonden"
          : breaches.length
            ? "Gevonden datalekken"
            : "Resultaatdetails"
      );
    }

    if (ui.breachesWrap) show(ui.breachesWrap);

    if (totalCount === 0) {
      ui.reportBreaches.innerHTML = "";
      return;
    }

    if (!breaches.length) {
      var genericCard = renderGenericFindingsCard(report);
      if (genericCard) {
        ui.reportBreaches.appendChild(genericCard);
        if (ui.breachesCard) show(ui.breachesCard);
        return;
      }

      var empty = document.createElement("div");
      empty.className = "ids-qsr__mutedline";
      empty.textContent = "Er zijn bekende signalen gevonden, maar de bron leverde geen losse detailkaarten mee.";
      ui.reportBreaches.appendChild(empty);
      if (ui.breachesCard) show(ui.breachesCard);
      return;
    }

    for (var i = 0; i < breaches.length; i++) {
      var b = breaches[i] || {};
      var tplNode = renderBreachCardFromTemplate(ui, b);
      if (tplNode) {
        ui.reportBreaches.appendChild(tplNode);
        continue;
      }

      var card = document.createElement("article");
      card.className = "ids-qsr__breach-card ids-qs__breach ids-qs__breach--fallback";
      card.setAttribute("data-breach-index", String(i));

      var head = document.createElement("div");
      head.className = "ids-qsr__breach-head";

      var h4 = document.createElement("h4");
      h4.className = "ids-qsr__breach-title ids-qs__breach-title";
      h4.textContent = b.name || "Onbekend lek";
      head.appendChild(h4);

      var meta = document.createElement("div");
      meta.className = "ids-qsr__breach-meta";
      if (b.date) {
        var spanDate = document.createElement("span");
        spanDate.textContent = formatDateNL(b.date);
        meta.appendChild(spanDate);
      }
      if (b.age_label_nl) {
        var spanAge = document.createElement("span");
        spanAge.textContent = b.age_label_nl;
        meta.appendChild(spanAge);
      }
      if (meta.childNodes.length) head.appendChild(meta);

      card.appendChild(head);

      var leaked = toStringArray(b.leaked_data_nl);
      if (leaked.length) {
        var chips = document.createElement("div");
        chips.className = "ids-qsr__chips";
        leaked.forEach(function (chip) {
          var s = document.createElement("span");
          s.className = "ids-qsr__chip ids-qs__chip2";
          s.textContent = chip;
          chips.appendChild(s);
        });
        card.appendChild(chips);
      }

      if (b.what_happened_nl) {
        var blk1 = document.createElement("div");
        blk1.className = "ids-qsr__block";
        var t1 = document.createElement("h5");
        t1.textContent = "Wat is er gebeurd?";
        blk1.appendChild(t1);
        var p1 = document.createElement("p");
        p1.textContent = b.what_happened_nl;
        blk1.appendChild(p1);
        card.appendChild(blk1);
      }

      var criminals = toStringArray(b.what_criminals_can_do_nl);
      if (criminals.length) {
        var blk2 = document.createElement("div");
        blk2.className = "ids-qsr__block";
        var t2 = document.createElement("h5");
        t2.textContent = "Waar je op kunt letten";
        blk2.appendChild(t2);
        var ul2 = document.createElement("ul");
        criminals.forEach(function (c) {
          var li = document.createElement("li");
          li.textContent = c;
          ul2.appendChild(li);
        });
        blk2.appendChild(ul2);
        card.appendChild(blk2);
      }

      var advice = toStringArray(b.advice_now_nl);
      if (advice.length) {
        var blk3 = document.createElement("div");
        blk3.className = "ids-qsr__block";
        var t3 = document.createElement("h5");
        t3.textContent = "Wat kun je nu doen?";
        blk3.appendChild(t3);
        var ul3 = document.createElement("ul");
        advice.forEach(function (a) {
          var li2 = document.createElement("li");
          li2.textContent = a;
          ul3.appendChild(li2);
        });
        blk3.appendChild(ul3);
        card.appendChild(blk3);
      }

      if (validHttpUrl(b.source_url)) {
        var src = document.createElement("div");
        src.className = "ids-qsr__block ids-qs__breach-sourcewrap";
        var a = document.createElement("a");
        a.href = b.source_url;
        a.target = "_blank";
        a.rel = "noopener";
        a.textContent = "Bron bekijken";
        src.appendChild(a);
        card.appendChild(src);
      }

      ui.reportBreaches.appendChild(card);
    }

    if (ui.breachesCard) show(ui.breachesCard);
  }

  function renderSeverity(ui, report) {
    var sev = severityView(report.severity_code);

    if (ui.reportSeverity) {
      ui.reportSeverity.setAttribute("data-severity-code", report.severity_code || "unknown");
      text(ui.reportSeverity, sev.label);
      setAttr(ui.reportSeverity, "aria-label", "Risiconiveau: " + sev.label);
    }

    if (ui.severityLabel) text(ui.severityLabel, sev.label);
    if (ui.scoreEl) text(ui.scoreEl, String(clamp(report.risk_score, 0, 100)));
    if (ui.scoreBar) {
      try {
        ui.scoreBar.style.width = clamp(report.risk_score, 0, 100) + "%";
      } catch (e) {}
    }
    if (ui.scoreNote) text(ui.scoreNote, sev.note);
    if (ui.scoreLabel && !ui.scoreLabel.textContent) text(ui.scoreLabel, "Risicoscore");
  }

  function renderOptionalIdentifiers(ui, meta) {
    if (ui.reportPhoneWrap && ui.reportPhone) {
      var hasPhone =
        String(meta.phone_provided || "") === "true" ||
        !!String(meta.phone_masked || "").trim();

      if (!hasPhone) {
        hide(ui.reportPhoneWrap);
      } else {
        var pCount = Number(meta.phone_breach_count || 0);
        if (!isFinite(pCount) || pCount < 0) pCount = 0;

        var pStatus = String(meta.phone_status || "").toLowerCase().trim();
        var pLabel = (pStatus === "clean" || pStatus === "clear" || pCount === 0) ? "Geen bekende match" : "Bekende signalen";
        var maskedPhone = String(meta.phone_masked || "").trim();

        text(
          ui.reportPhone,
          maskedPhone
            ? maskedPhone + " • " + pLabel + (pCount > 0 ? " • " + String(pCount) + " gevonden" : "")
            : pLabel + (pCount > 0 ? " • " + String(pCount) + " gevonden" : "")
        );
        show(ui.reportPhoneWrap);
      }
    }

    if (ui.reportUsernameWrap && ui.reportUsername) {
      var hasUsername =
        String(meta.username_provided || "") === "true" ||
        !!String(meta.username_masked || "").trim();

      if (!hasUsername) {
        hide(ui.reportUsernameWrap);
      } else {
        var uCount = Number(meta.username_breach_count || 0);
        if (!isFinite(uCount) || uCount < 0) uCount = 0;

        var uStatus = String(meta.username_status || "").toLowerCase().trim();
        var uLabel = (uStatus === "clean" || uStatus === "clear" || uCount === 0) ? "Geen bekende match" : "Bekende signalen";
        var maskedUsername = String(meta.username_masked || "").trim();

        text(
          ui.reportUsername,
          maskedUsername
            ? maskedUsername + " • " + uLabel + (uCount > 0 ? " • " + String(uCount) + " gevonden" : "")
            : uLabel + (uCount > 0 ? " • " + String(uCount) + " gevonden" : "")
        );
        show(ui.reportUsernameWrap);
      }
    }
  }

  function renderMeta(ui, report, meta, sourceEmail) {
    var emailMasked = report.email_masked || (meta && meta.email_masked) || maskEmail(sourceEmail || "");

    var count = Number(report.total_match_count || (meta && meta.total_match_count) || (meta && meta.breach_count) || 0);
    if (!isFinite(count) || count < 0) count = 0;

    var lastSeen = report.latest_breach_date_label || (meta && meta.last_seen_at ? formatDateNL(meta.last_seen_at) : "—");

    if (ui.reportEmail) text(ui.reportEmail, emailMasked || "—");
    if (ui.reportCount) text(ui.reportCount, String(count));
    if (ui.reportLastSeen) text(ui.reportLastSeen, lastSeen);

    renderOptionalIdentifiers(ui, meta || {});
  }

  function renderPdfButton(ui, report) {
    if (!ui.pdfBtn) return;

    show(ui.pdfBtn);

    var url = String((report && report.pdf_url) || "").trim();
    if (url && validHttpUrl(url)) {
      ui.pdfBtn.dataset.qsPdfUrl = url;
    } else {
      try {
        delete ui.pdfBtn.dataset.qsPdfUrl;
      } catch (e) {}
    }

    ui.pdfBtn.dataset.qsPdfMode = "print";
  }

  function renderPlaybook(ui, report, cfg) {
    if (!ui.playbookHost) {
      if (ui.playbookWrap) hide(ui.playbookWrap);
      return;
    }

    ui.playbookHost.innerHTML = "";

    var items = Array.isArray(report.playbook_items_nl) ? report.playbook_items_nl : [];
    if (!items.length) {
      if (ui.playbookWrap) hide(ui.playbookWrap);
      return;
    }

    if (ui.playbookWrap) show(ui.playbookWrap);

    var fallbackUrl =
      ((cfg || {}).tools && (cfg || {}).tools.url_checker_url) ||
      (cfg || {}).url_checker_url ||
      "/pages/url-checker";

    items.forEach(function (item) {
      var normalized = normalizePlaybookItem(item) || item;
      var severityInfo = severityView(normalized.severity_code || "medium");

      if (ui.playbookTemplate && "content" in ui.playbookTemplate) {
        var frag = ui.playbookTemplate.content.cloneNode(true);

        text(qs(frag, "[data-pb-title]"), normalized.title || "Uitleg");
        var sevEl = qs(frag, "[data-pb-sev]");
        if (sevEl) {
          sevEl.setAttribute("data-severity-code", normalizeSeverityCode(normalized.severity_code || "medium"));
          text(sevEl, severityInfo.label);
        }

        text(qs(frag, "[data-pb-meaning]"), normalized.meaning || "");
        fillList(qs(frag, "[data-pb-actions]"), normalized.actions || []);

        var cta = qs(frag, "[data-pb-cta]");
        if (cta) {
          if (normalized.cta_label) text(cta, normalized.cta_label);
          if (validHttpUrl(normalized.cta_url)) cta.href = normalized.cta_url;
          else cta.href = fallbackUrl;
        }

        ui.playbookHost.appendChild(frag);
        return;
      }

      var card = document.createElement("div");
      card.className = "ids-qsr__card";

      var head = document.createElement("div");
      head.className = "ids-qsr__card-head";

      var title = document.createElement("h3");
      title.className = "ids-qsr__card-title";
      title.textContent = normalized.title || "Uitleg";
      head.appendChild(title);

      var badge = document.createElement("span");
      badge.className = "ids-qsr__sev-badge";
      badge.setAttribute("data-severity-code", normalizeSeverityCode(normalized.severity_code || "medium"));
      badge.textContent = severityInfo.label;
      head.appendChild(badge);

      card.appendChild(head);

      var meaning = document.createElement("p");
      meaning.className = "ids-qsr__muted";
      meaning.textContent = normalized.meaning || "";
      card.appendChild(meaning);

      var ul = document.createElement("ul");
      ul.className = "ids-qsr__plain-list";
      toStringArray(normalized.actions).forEach(function (action) {
        var li = document.createElement("li");
        li.textContent = action;
        ul.appendChild(li);
      });
      card.appendChild(ul);

      ui.playbookHost.appendChild(card);
    });
  }

  function renderErrorMessage(ui, msg) {
    msg = msg || "We konden je rapport niet tonen. Probeer opnieuw.";
    if (ui.errorShell) {
      var msgNode = qs(ui.errorShell, ".ids-qsr__error-text");
      if (msgNode) text(msgNode, msg);
      else if (!ui.errorShell.textContent || !ui.errorShell.textContent.trim())
        html(ui.errorShell, '<p class="ids-qsr__error-text">' + escapeHtml(msg) + "</p>");
      show(ui.errorShell);
    }
    if (ui.summaryHost && !ui.summaryHost.textContent.trim()) {
      var p = document.createElement("p");
      p.textContent = msg;
      ui.summaryHost.appendChild(p);
    }
  }

  function maybeAutoOpenSuccessOffer(ui, sessionId, report) {
    if (!ui || !ui.successOfferModal) return;
    if (Number(report.total_match_count || 0) !== 0) return;
    if (readUiFlag(sessionId, "success_offer_opened") === "1") return;

    writeUiFlag(sessionId, "success_offer_opened");

    window.setTimeout(function () {
      try {
        if (typeof ui.successOfferModal.showModal === "function") ui.successOfferModal.showModal();
        else ui.successOfferModal.setAttribute("open", "open");
        pushDL("qs_success_offer_modal_auto_open", { session_id: sessionId });
      } catch (e) {}
    }, 250);
  }

  function renderReport(ui, normalized, ctx) {
    ctx = ctx || {};
    var report = normalized.report;
    var meta = normalized.meta || {};
    var totalCount = Number(report.total_match_count || meta.total_match_count || meta.breach_count || 0);

    if (ui.reportRoot) show(ui.reportRoot);
    if (ui.reportHeadline) text(ui.reportHeadline, report.headline_nl || "QuickScan rapport");

    renderResultHero(ui, report);
    renderSummary(ui, report);
    renderActions(ui, report);
    renderLimitations(ui, report);
    renderBreaches(ui, report);
    renderSeverity(ui, report);
    renderMeta(ui, report, meta, ctx.email);
    renderPdfButton(ui, report);
    renderPlaybook(ui, report, ctx.cfg || {});
    renderNoMatch(ui, report);

    if (ui.offerBlock) {
      if (totalCount === 0) show(ui.offerBlock);
      else hide(ui.offerBlock);
    }

    setStatusTextAll(
      ui,
      totalCount === 0 ? "Geen bekende matches gevonden." : "Je rapport is klaar."
    );
  }

  // ------------------------------------------------------------
  // LANDING PAGE FLOW
  // ------------------------------------------------------------
  function initLandingPage(root, cfg, ui) {
    if (!ui.form) return;
    if (ui.form.dataset.idsQsBound === "1") return;
    ui.form.dataset.idsQsBound = "1";

    var landingLocked = false;
    var confirmBusy = false;
    var confirmResetTimer = null;

    function focusSafe(el) {
      try {
        if (el && typeof el.focus === "function") el.focus();
      } catch (e) {}
    }

    function clearConfirmResetTimer() {
      if (!confirmResetTimer) return;
      try {
        window.clearTimeout(confirmResetTimer);
      } catch (e) {}
      confirmResetTimer = null;
    }

    function setStatus(msg) {
      if (ui.status) text(ui.status, msg || "");
    }

    function clearErrors() {
      if (ui.errEmail) text(ui.errEmail, "");
      if (ui.errOwn) text(ui.errOwn, "");
      if (ui.errConsent) text(ui.errConsent, "");
      if (ui.emailInput) removeAttr(ui.emailInput, "aria-invalid");
    }

    function setLandingLocked(next) {
      landingLocked = !!next;

      if (ui.submitBtn) {
        ui.submitBtn.disabled = landingLocked;
        setAttr(ui.submitBtn, "aria-busy", landingLocked ? "true" : "false");
      }

      if (ui.form) {
        ui.form.dataset.qsLocked = landingLocked ? "1" : "0";
      }
    }

    function getConfirmButtons() {
      if (!ui.confirmOverlay) {
        return {
          continueBtn: null,
          editBtn: null,
          closeBtn: null,
          manualPayLink: null
        };
      }

      return {
        continueBtn: qs(ui.confirmOverlay, "[data-ids-qs-confirm-continue], [data-qs-confirm-continue]"),
        editBtn: qs(ui.confirmOverlay, "[data-ids-qs-confirm-edit], [data-qs-confirm-edit]"),
        closeBtn: qs(ui.confirmOverlay, "[data-ids-qs-confirm-close], [data-qs-confirm-close]"),
        manualPayLink: qs(ui.confirmOverlay, "[data-ids-qs-manual-pay]")
      };
    }

    function setConfirmBusy(next) {
      confirmBusy = !!next;

      var btns = getConfirmButtons();
      var continueBtn = btns.continueBtn;
      var editBtn = btns.editBtn;
      var closeBtn = btns.closeBtn;

      [continueBtn, editBtn, closeBtn].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = confirmBusy;
        setAttr(btn, "aria-busy", confirmBusy ? "true" : "false");
      });

      if (!continueBtn) return;

      var label = qs(continueBtn, ".ids-qs__btn-label");
      var sub = qs(continueBtn, ".ids-qs__btn-sub");

      if (confirmBusy) {
        if (label && !label.dataset.originalText) label.dataset.originalText = label.textContent || "";
        if (sub && !sub.dataset.originalText) sub.dataset.originalText = sub.textContent || "";

        if (label) label.textContent = "Bezig met openen…";
        if (sub) sub.textContent = "Je wordt doorgestuurd naar Stripe";
      } else {
        if (label && label.dataset.originalText) label.textContent = label.dataset.originalText;
        if (sub && sub.dataset.originalText) sub.textContent = sub.dataset.originalText;
      }
    }

    function storeIntent(data) {
      data = data || {};

      var email = String(data.email || "").trim();
      var phone = String(data.phone || "").trim();
      var username = String(data.username || "").trim();
      var consentBool = String(data.consent_bool || "").trim();

      tryLocalStorageSet(STORAGE.email, email);
      tryLocalStorageSet(STORAGE.intent, "pending_payment");
      tryLocalStorageSet(STORAGE.startedAt, String(now()));

      var resultsAbs =
        ((cfg || {}).paths && (cfg || {}).paths.results_abs_url) ||
        (cfg || {}).results_abs_url ||
        "";
      tryLocalStorageSet(STORAGE.resultsAbsUrl, resultsAbs);

      tryLocalStorageSet(STORAGE.testMode, ((cfg || {}).flags || {}).test_mode ? "1" : "0");
      tryLocalStorageSet(STORAGE.forceTrigger, "1");

      if (phone) tryLocalStorageSet(STORAGE.phone, phone);
      else tryLocalStorageRemove(STORAGE.phone);

      if (username) tryLocalStorageSet(STORAGE.username, username);
      else tryLocalStorageRemove(STORAGE.username);

      getOrCreateIdempotencyKey(String((cfg && cfg.section_id) || "qs"));

      if (consentBool) dualSet(STORAGE.consentBool, consentBool);
    }

    function buildStripeUrl(email) {
      var base =
        (((cfg || {}).payment || {}).stripe_payment_link) ||
        (cfg || {}).stripe_payment_link ||
        "";

      if (!base) return "";

      try {
        var u = new URL(base, window.location.origin);
        var param =
          (((cfg || {}).payment || {}).prefilled_email_param) ||
          (cfg || {}).prefilled_email_param ||
          "prefilled_email";

        if (email) u.searchParams.set(param, email);

        // Attribution pass-through: forward gclid/UTM from landing page to Stripe
        // Reads from current URL first; falls back to sessionStorage (set by cybercheck-pricing.liquid)
        try {
          var _sp = new URLSearchParams(window.location.search || "");
          ["gclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"].forEach(function(k) {
            var v = _sp.get(k) || "";
            if (!v) { try { v = sessionStorage.getItem("idns_attr_" + k) || ""; } catch (_se) {} }
            if (v) u.searchParams.set(k, v);
          });
        } catch (_ae) {}

        return u.toString();
      } catch (e) {
        return String(base || "").trim();
      }
    }

    function ensureConfirmExtraHost() {
      if (!ui.confirmOverlay) return null;
      if (ui.confirmExtraHost) return ui.confirmExtraHost;

      var dlg = qs(ui.confirmOverlay, "[data-ids-qs-confirm-dialog]") || ui.confirmOverlay;
      if (!dlg) return null;

      var host = document.createElement("div");
      host.setAttribute("data-ids-confirm-extra", "");
      host.className = "ids-qs__confirm-extra";

      if (ui.confirmEmail && ui.confirmEmail.parentNode) {
        ui.confirmEmail.parentNode.insertBefore(host, ui.confirmEmail.nextSibling);
      } else {
        dlg.appendChild(host);
      }

      ui.confirmExtraHost = host;
      return host;
    }

    function renderConfirmDetails(email, phone, username) {
      if (!ui.confirmOverlay) return;

      if (ui.confirmEmail) text(ui.confirmEmail, maskEmail(email));
      if (ui.confirmPrice) {
        text(
          ui.confirmPrice,
          (((cfg || {}).prices || {}).one_time) ||
            (cfg || {}).one_time_price ||
            "€2,99"
        );
      }

      if (ui.confirmPhone) text(ui.confirmPhone, phone ? maskPhone(phone) : "—");
      if (ui.confirmUsername) text(ui.confirmUsername, username ? String(username).trim() : "—");

      var manualPayLink = getConfirmButtons().manualPayLink;
      if (manualPayLink) {
        var href = buildStripeUrl(email);
        if (href) manualPayLink.href = href;
      }

      var hasAnyExtra = !!String(phone || "").trim() || !!String(username || "").trim();

      if (!ui.confirmPhone && !ui.confirmUsername) {
        var host = ensureConfirmExtraHost();
        if (host) {
          host.innerHTML = "";

          if (hasAnyExtra) {
            var ul = document.createElement("ul");
            ul.className = "ids-qs__confirm-extra-list";

            if (phone) {
              var li1 = document.createElement("li");
              li1.textContent = "Telefoon: " + maskPhone(phone);
              ul.appendChild(li1);
            }

            if (username) {
              var li2 = document.createElement("li");
              li2.textContent = "Username: " + String(username).trim();
              ul.appendChild(li2);
            }

            host.appendChild(ul);
          }
        }
      }
    }

    function openConfirm(email, phone, username) {
      if (!ui.confirmOverlay) return false;

      clearConfirmResetTimer();
      setConfirmBusy(false);
      renderConfirmDetails(email, phone, username);

      show(ui.confirmOverlay);
      ui.confirmOverlay.dataset.open = "1";

      var dlg = qs(ui.confirmOverlay, "[data-ids-qs-confirm-dialog]") || ui.confirmOverlay;
      focusSafe(dlg);

      return true;
    }

    function closeConfirm() {
      if (!ui.confirmOverlay) return;
      clearConfirmResetTimer();
      hide(ui.confirmOverlay);
      ui.confirmOverlay.dataset.open = "0";
      setConfirmBusy(false);
    }

    function scheduleConfirmRecovery() {
      clearConfirmResetTimer();

      confirmResetTimer = window.setTimeout(function () {
        if (document.visibilityState === "visible") {
          setConfirmBusy(false);
          setLandingLocked(false);
          setStatus("Het openen van Stripe duurt langer dan verwacht. Probeer opnieuw.");
        }
      }, 2500);
    }

    function redirectToUrl(url) {
      if (!url) return false;

      try {
        window.location.assign(url);
        return true;
      } catch (err) {
        console.error("[ids-quickscan] redirect failed:", err);
        return false;
      }
    }

    function startRedirect(data) {
      if (landingLocked) return false;

      setLandingLocked(true);

      data = data || {};
      var email = String(data.email || "").trim();

      storeIntent(data);

      var isTest = !!((cfg || {}).flags || {}).test_mode || asBool((cfg || {}).test_mode);

      if (isTest) {
        var results =
          ((cfg || {}).paths && (cfg || {}).paths.results_abs_url) ||
          (cfg || {}).results_abs_url ||
          "";

        if (!results) {
          setStatus("Resultatenpagina ontbreekt.");
          setLandingLocked(false);
          return false;
        }

        var sid = "test_" + Date.now();

        try {
          var ru = new URL(results, window.location.origin);
          ru.searchParams.set("session_id", sid);
          ru.searchParams.set("test", "1");

          setStatus("Testmodus: resultatenpagina openen...");
          pushDL("qs_test_redirect", { section_id: cfg.section_id || "" });

          if (!redirectToUrl(ru.toString())) {
            setLandingLocked(false);
            setStatus("Testresultaten konden niet worden geopend.");
            return false;
          }

          return true;
        } catch (e) {
          var fallbackUrl =
            results +
            "?session_id=" +
            encodeURIComponent(sid) +
            "&test=1";

          setStatus("Testmodus: resultatenpagina openen...");
          pushDL("qs_test_redirect", { section_id: cfg.section_id || "" });

          if (!redirectToUrl(fallbackUrl)) {
            setLandingLocked(false);
            setStatus("Testresultaten konden niet worden geopend.");
            return false;
          }

          return true;
        }
      }

      var stripeUrl = buildStripeUrl(email);
      if (!stripeUrl) {
        setStatus("Betaling is tijdelijk niet beschikbaar.");
        setLandingLocked(false);
        pushDL("qs_error_payment_link_missing", { section_id: cfg.section_id || "" });
        return false;
      }

      setStatus("We sturen je door naar de betaling...");
      pushDL("qs_redirect_stripe", {
        section_id: cfg.section_id || "",
        has_email: true,
        has_phone: !!String(data.phone || "").trim(),
        has_username: !!String(data.username || "").trim()
      });

      // GA4 standard begin_checkout — real intent moment: user confirmed and redirect is imminent
      try {
        if (typeof window.gtag === "function") {
          var _bcVal = resolveQuickScanValue(cfg);
          var _bcCur = resolveQuickScanCurrency(cfg);
          window.gtag("event", "begin_checkout", {
            send_to: "G-2N97MFC52L",
            currency: _bcCur,
            value: _bcVal,
            items: [{
              item_id: "quickscan",
              item_name: "IdenScan QuickScan",
              item_category: "One-time security scan",
              price: _bcVal,
              quantity: 1
            }]
          });
        }
      } catch (_be) {}

      if (!redirectToUrl(stripeUrl)) {
        setStatus("We konden de betaling niet openen. Probeer opnieuw.");
        setLandingLocked(false);
        pushDL("qs_redirect_stripe_failed", {
          section_id: cfg.section_id || ""
        });
        return false;
      }

      return true;
    }

    function readCurrentFormData() {
      var email = ((ui.emailInput && ui.emailInput.value) || "").trim();
      var phone = ui.phoneInput ? normalizePhone(ui.phoneInput.value || "") : "";
      var username = ui.usernameInput ? normalizeUsername(ui.usernameInput.value || "") : "";
      var consentChecked = !!(ui.consentCb && ui.consentCb.checked);

      return {
        email: email,
        phone: phone,
        username: username,
        consent_bool: consentChecked ? "true" : "false"
      };
    }

    function validate() {
      clearErrors();

      var data = readCurrentFormData();
      var ok = true;
      var firstBad = null;

      if (!isValidEmail(data.email)) {
        ok = false;
        if (ui.errEmail) text(ui.errEmail, "Vul een geldig e-mailadres in.");
        if (ui.emailInput) setAttr(ui.emailInput, "aria-invalid", "true");
        firstBad = firstBad || ui.emailInput;
      }

      if (!ui.ownCb || !ui.ownCb.checked) {
        ok = false;
        if (ui.errOwn) text(ui.errOwn, "Bevestig dat dit je eigen e-mail is (of met toestemming).");
        firstBad = firstBad || ui.ownCb;
      }

      var consentChecked = !!(ui.consentCb && ui.consentCb.checked);
      if (!consentChecked) {
        ok = false;
        if (ui.errConsent) text(ui.errConsent, "Ga akkoord met voorwaarden en privacy.");
        firstBad = firstBad || ui.consentCb;
      }

      if (ui.consentBool) ui.consentBool.value = consentChecked ? "true" : "false";
      dualSet(STORAGE.consentBool, consentChecked ? "true" : "false");

      if (!ok) {
        setStatus("Controleer de velden hierboven.");
        pushDL("qs_validation_error", { section_id: cfg.section_id || "" });
        focusSafe(firstBad);
        return null;
      }

      getOrCreateIdempotencyKey(String((cfg && cfg.section_id) || "qs"));

      setStatus("");
      return data;
    }

    function handleSubmitAttempt() {
      if (landingLocked || confirmBusy) return;

      pushDL("qs_submit_attempt", { section_id: cfg.section_id || "" });

      var data = validate();
      if (!data) return;

      var confirmEnabled =
        !!((cfg || {}).flags || {}).confirm_enabled ||
        asBool((cfg || {}).confirm_enabled);

      if (confirmEnabled && ui.confirmOverlay) {
        openConfirm(data.email, data.phone, data.username);
        return;
      }

      startRedirect(data);
    }

    function handleConfirmContinue() {
      if (landingLocked || confirmBusy) return;

      var data = readCurrentFormData();

      if (!isValidEmail(data.email)) {
        closeConfirm();
        setLandingLocked(false);
        setStatus("Je e-mailadres is niet geldig. Controleer je invoer.");
        focusSafe(ui.emailInput);
        return;
      }

      setConfirmBusy(true);
      setStatus("We openen de betaling…");

      pushDL("qs_confirm_continue_click", {
        section_id: cfg.section_id || "",
        has_phone: !!data.phone,
        has_username: !!data.username
      });

      var ok = startRedirect(data);

      if (!ok) {
        setConfirmBusy(false);
        setLandingLocked(false);
        setStatus("We konden de betaling niet openen. Probeer opnieuw.");
        return;
      }

      scheduleConfirmRecovery();
    }

    ui.form.addEventListener(
      "submit",
      function (ev) {
        ev.preventDefault();
        handleSubmitAttempt();
      },
      true
    );

    if (ui.confirmOverlay) {
      ui.confirmOverlay.addEventListener(
        "click",
        function (ev) {
          var t = ev.target;
          if (!t) return;

          var closeBtn = t.closest
            ? t.closest("[data-ids-qs-confirm-close], [data-qs-confirm-close]")
            : null;
          var editBtn = t.closest
            ? t.closest("[data-ids-qs-confirm-edit], [data-qs-confirm-edit]")
            : null;
          var continueBtn = t.closest
            ? t.closest("[data-ids-qs-confirm-continue], [data-qs-confirm-continue]")
            : null;

          if (closeBtn) {
            ev.preventDefault();
            closeConfirm();
            setLandingLocked(false);
            return;
          }

          if (editBtn) {
            ev.preventDefault();
            closeConfirm();
            setLandingLocked(false);
            focusSafe(ui.emailInput);
            return;
          }

          if (continueBtn) {
            ev.preventDefault();
            handleConfirmContinue();
            return;
          }
        },
        true
      );

      ui.confirmOverlay.addEventListener("keydown", function (ev) {
        if (!ev) return;
        if (ev.key === "Escape" && !confirmBusy) {
          closeConfirm();
          setLandingLocked(false);
        }
      });
    }
  }

  // ------------------------------------------------------------
  // RESULT PAGE FLOW
  // ------------------------------------------------------------
  function getQueryParam(name) {
    try {
      var u = new URL(window.location.href);
      return u.searchParams.get(name);
    } catch (e) {
      return null;
    }
  }

  function getSessionIdFromUrl() {
    var sid = getQueryParam("session_id");
    if (!sid) return "";
    sid = String(sid).trim();
    if (sid.length < 4) return "";
    return sid;
  }

  function readCachedNormalized(sessionId) {
    var raw = tryLocalStorageGet(storageKey(STORAGE.sessionCachePrefix, sessionId));
    if (!raw) return null;

    var parsed = safeJSONParse(raw, null);
    if (!isObj(parsed)) return null;
    if (!parsed.report || !parsed.meta) return null;

    var schemaOk = parsed._cache_schema === CACHE_SCHEMA_VERSION;
    var explicitReport = parsed.meta && parsed.meta._explicit_report === true;

    if (!schemaOk || !explicitReport) {
      tryLocalStorageRemove(storageKey(STORAGE.sessionCachePrefix, sessionId));
      tryLocalStorageRemove(storageKey(STORAGE.sessionDonePrefix, sessionId));
      return null;
    }

    if (!hasRenderableReport(parsed)) {
      tryLocalStorageRemove(storageKey(STORAGE.sessionCachePrefix, sessionId));
      tryLocalStorageRemove(storageKey(STORAGE.sessionDonePrefix, sessionId));
      return null;
    }

    return parsed;
  }

  function writeCachedNormalized(sessionId, normalized) {
    if (!normalized || !hasRenderableReport(normalized)) return;

    var toStore = Object.assign({}, normalized, {
      _cache_schema: CACHE_SCHEMA_VERSION
    });

    tryLocalStorageSet(
      storageKey(STORAGE.sessionCachePrefix, sessionId),
      JSON.stringify(toStore)
    );
    tryLocalStorageSet(storageKey(STORAGE.sessionDonePrefix, sessionId), "1");

    writeSessionMeta(sessionId, {
      ts: now(),
      status: "done",
      job_id: normalized && normalized.meta ? normalized.meta.job_id || "" : "",
      explicit_report: !!(normalized && normalized.meta && normalized.meta._explicit_report)
    });

    writeTriggerAck(sessionId, {
      ts: now(),
      status: "done",
      accepted: true,
      job_id:
        normalized &&
        normalized.meta &&
        normalized.meta.job_id
          ? String(normalized.meta.job_id)
          : "",
      done: true
    });
  }

  function getLock(sessionId) {
    var raw = tryLocalStorageGet(storageKey(STORAGE.sessionLockPrefix, sessionId));
    var parsed = safeJSONParse(raw, null);
    if (!isObj(parsed)) return null;
    return parsed;
  }

  function setLock(sessionId, meta) {
    var payload = Object.assign({ ts: now() }, meta || {});
    tryLocalStorageSet(storageKey(STORAGE.sessionLockPrefix, sessionId), JSON.stringify(payload));
  }

  function clearLock(sessionId) {
    tryLocalStorageRemove(storageKey(STORAGE.sessionLockPrefix, sessionId));
  }

  function lockIsFresh(lockObj) {
    if (!lockObj || !isObj(lockObj)) return false;
    var ts = Number(lockObj.ts);
    if (!isFinite(ts)) return false;
    return now() - ts < TIMING.LOCK_TTL_MS;
  }

  function buildTriggerPayload(cfg, sessionId, contact) {
    contact = contact || {};

    var email = String(contact.email || "").trim();
    var phone = String(contact.phone || "").trim();
    var username = String(contact.username || "").trim();

    var nowMs = safeNowMs();
    var startedAtMs = getStoredStartedAtMs();
    var isTest = getQueryParam("test") === "1";

    var idemBase = getOrCreateIdempotencyKey(String((cfg && cfg.section_id) || "qs"));
    var jobId = getStableJobIdFromIdem(sessionId);

    if (!jobId || jobId.length < 11) jobId = idemBase;

    var consentBool = getStoredConsentBool() || "true";

    var payload = {
      input_email: email,
      idempotency_key: idemBase,
      job_id: jobId,

      ctx_channel: "quickscan",
      consent: "on",
      consent_bool: consentBool,

      hp: "",
      honeypot: "",
      company: "",

      page_url: window.location.href,
      referrer: document.referrer || "",
      ua: (navigator && navigator.userAgent) ? navigator.userAgent : "",

      attempt_ts: String(nowMs),
      form_started_at: String(startedAtMs),
      client_ts: new Date(nowMs).toISOString(),

      mode: "start",
      session_id: sessionId,
      stripe_session_id: sessionId,
      paid_return: "true",
      plan: "quickscan",
      plan_label: "QuickScan",
      tz_name: "Europe/Amsterdam",
      test_mode: isTest ? "true" : "false"
    };

    if (phone) payload.input_phone = phone;
    if (username) payload.input_username = username;

    setEmailFlags(payload, email);

    var resultsAbsUrl =
      ((cfg || {}).paths && (cfg || {}).paths.results_abs_url) ||
      (cfg || {}).results_abs_url ||
      tryLocalStorageGet(STORAGE.resultsAbsUrl) ||
      "";

    if (resultsAbsUrl) payload.results_abs_url = resultsAbsUrl;
    if (cfg && cfg.section_id) payload.section_id = String(cfg.section_id);

    return payload;
  }

  function buildPollPayload(cfg, sessionId, email, pollSeq) {
    var nowMs = safeNowMs();
    var isTest = getQueryParam("test") === "1";

    var jobId = getStableJobIdFromIdem(sessionId);
    var bucket = Math.floor(nowMs / 20000);

    var idemBase = String(dualGet(STORAGE.idempotencyKey) || "").trim();
    if (!idemBase || idemBase.length < 11) {
      idemBase = getOrCreateIdempotencyKey(String((cfg && cfg.section_id) || "qs"));
    }

    var idem = idemBase + "_poll_" + bucket;

    var payload = {
      input_email: String(email || "").trim(),
      idempotency_key: idem,
      job_id: jobId,

      ctx_channel: "quickscan",
      consent: "on",
      consent_bool: getStoredConsentBool() || "true",

      hp: "",
      honeypot: "",
      company: "",

      page_url: window.location.href,
      referrer: document.referrer || "",
      ua: (navigator && navigator.userAgent) ? navigator.userAgent : "",

      attempt_ts: String(nowMs),
      form_started_at: String(getStoredStartedAtMs()),
      client_ts: new Date(nowMs).toISOString(),

      mode: "result",
      only_result: "true",
      poll_seq: String(pollSeq || 0),
      session_id: sessionId,

      tz_name: "Europe/Amsterdam",
      test_mode: isTest ? "true" : "false"
    };

    setEmailFlags(payload, email);

    if (cfg && cfg.section_id) payload.section_id = String(cfg.section_id);

    return payload;
  }

  function bindResultButtons(ui, ctx) {
    if (ui.retryBtn && !ui.retryBtn.dataset.idsQsRetryBound) {
      ui.retryBtn.dataset.idsQsRetryBound = "1";
      ui.retryBtn.addEventListener("click", function (ev) {
        ev.preventDefault();
        if (typeof ctx.retry === "function") ctx.retry();
      });
    }

    if (ui.pdfBtn && !ui.pdfBtn.dataset.idsQsPdfBound) {
      ui.pdfBtn.dataset.idsQsPdfBound = "1";
      ui.pdfBtn.addEventListener("click", function (ev) {
        ev.preventDefault();

        var root = ui && ui.root ? ui.root : document;

        var detailsEls = qsa(root, "details");
        var dialogsEls = qsa(root, "dialog.ids-qsr__modal");

        var detailsWasOpen = [];
        var dialogsWasOpen = [];

        for (var i = 0; i < detailsEls.length; i++) {
          detailsWasOpen.push(detailsEls[i].hasAttribute("open"));
          try {
            detailsEls[i].setAttribute("open", "open");
          } catch (e1) {}
        }

        for (var j = 0; j < dialogsEls.length; j++) {
          dialogsWasOpen.push(dialogsEls[j].hasAttribute("open"));
          try {
            dialogsEls[j].setAttribute("open", "open");
          } catch (e2) {}
        }

        var cleaned = false;
        function cleanup() {
          if (cleaned) return;
          cleaned = true;

          for (var a = 0; a < detailsEls.length; a++) {
            try {
              if (!detailsWasOpen[a]) detailsEls[a].removeAttribute("open");
            } catch (e3) {}
          }

          for (var b = 0; b < dialogsEls.length; b++) {
            try {
              if (!dialogsWasOpen[b]) dialogsEls[b].removeAttribute("open");
            } catch (e4) {}
          }
        }

        try {
          window.addEventListener("afterprint", cleanup, { once: true });
        } catch (e5) {}

        setTimeout(cleanup, 15000);

        try {
          window.print();
        } catch (e6) {
          cleanup();
        }
      });
    }

    if (ui.offerCopyBtn && !ui.offerCopyBtn.dataset.idsQsCopyBound) {
      ui.offerCopyBtn.dataset.idsQsCopyBound = "1";
      ui.offerCopyBtn.addEventListener("click", function (ev) {
        ev.preventDefault();

        var code = ((ui.offerCode && ui.offerCode.textContent) || "").trim();
        if (!code) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(code)
            .then(function () {
              text(ui.offerCopyBtn, "Gekopieerd");
              setTimeout(function () {
                text(ui.offerCopyBtn, "Kopieer");
              }, 1800);
            })
            .catch(function () {});
        }
      });
    }
  }

  // ------------------------------------------------------------
  // Make Orchestrator response handling
  // ------------------------------------------------------------
  function mapHttpToUserMessage(status) {
    if (status === 202) return { kind: "ignored", ui: "Verzoek genegeerd", detail: "Het verzoek is niet verwerkt." };
    if (status === 403) return { kind: "consent", ui: "Consent vereist", detail: "Je moet akkoord gaan met voorwaarden en privacy." };
    if (status === 400) return { kind: "invalid", ui: "Ongeldige invoer", detail: "Controleer je e-mailadres en probeer opnieuw." };
    return { kind: "server", ui: "Serverfout", detail: "Er ging iets mis. Probeer opnieuw." };
  }

  function resolveWebhookTargets(cfg) {
    var flags = (cfg || {}).flags || {};
    var makeCfg = (cfg || {}).make || {};
    var webhooks = (cfg || {}).webhooks || {};

    function pick() {
      for (var i = 0; i < arguments.length; i++) {
        var v = String(arguments[i] || "").trim();
        if (v) return v;
      }
      return "";
    }

    var orchestratorUrl = pick(
      makeCfg.orchestrator_webhook_url,
      (cfg || {}).orchestrator_webhook_url,
      webhooks.orchestrator_url,
      tryLocalStorageGet(STORAGE.makeUrl)
    );

    var resultUrl = pick(
      makeCfg.result_webhook_url,
      (cfg || {}).result_webhook_url,
      webhooks.result_url,
      webhooks.poll_url
    );

    var workerFallbackUrl = pick(
      makeCfg.worker_fallback_url,
      (cfg || {}).worker_fallback_url,
      makeCfg.worker_url,
      (cfg || {}).worker_url
    );

    var sameEndpointOptIn = asBool(flags.allow_same_endpoint_mode_router || (cfg || {}).allow_same_endpoint_mode_router);

    // Worker-first: if primary is Make and worker fallback exists, prefer worker
    var primaryIsMake = isProbablyMakeWebhookUrl(orchestratorUrl);
    if (primaryIsMake && workerFallbackUrl) {
      pushDL("qs_worker_first_override", { reason: "primary_is_make", has_worker_fallback: true });
      orchestratorUrl = workerFallbackUrl;
      if (!resultUrl && sameEndpointOptIn) resultUrl = workerFallbackUrl;
    }

    // If resultUrl missing, we allow same-endpoint polling as best-effort fallback
    var implicitSameEndpoint = false;
    if (!resultUrl && orchestratorUrl) {
      resultUrl = orchestratorUrl;
      implicitSameEndpoint = true;
    }

    if (!orchestratorUrl) return { ok: false, error: "missing_orchestrator_url", orchestratorUrl: "", resultUrl: "", workerFallbackUrl: "" };
    if (!resultUrl) return { ok: false, error: "missing_result_url", orchestratorUrl: orchestratorUrl, resultUrl: "", workerFallbackUrl: workerFallbackUrl };

    if (sameEndpointUrl(resultUrl, orchestratorUrl) && !sameEndpointOptIn && !implicitSameEndpoint) {
      return { ok: false, error: "same_endpoint_blocked", orchestratorUrl: orchestratorUrl, resultUrl: resultUrl, workerFallbackUrl: workerFallbackUrl };
    }

    return {
      ok: true,
      orchestratorUrl: orchestratorUrl,
      resultUrl: resultUrl,
      workerFallbackUrl: workerFallbackUrl,
      sameEndpointOptIn: sameEndpointOptIn,
      implicitSameEndpoint: implicitSameEndpoint
    };
  }

  function initResultPage(root, cfg, ui) {
    var sessionId = getSessionIdFromUrl();
    var testMode = getQueryParam("test") === "1";
    var email = (tryLocalStorageGet(STORAGE.email) || "").trim();
    var phone = (tryLocalStorageGet(STORAGE.phone) || "").trim();
    var username = (tryLocalStorageGet(STORAGE.username) || "").trim();

    function setState(state, opts) {
      setResultUiState(ui, state, opts || {});
    }

    if (!sessionId) {
      setState(QS_UI_STATES.ERROR, {
        statusText: "Geen betaalreferentie gevonden. Open QuickScan opnieuw en rond de betaling af.",
        showRetry: false
      });
      renderErrorMessage(ui, "Er ontbreekt een session_id in de URL. Daardoor kunnen we de scan niet starten.");
      pushDL("qs_result_missing_session_id", {});
      return;
    }

    if (!testMode && !looksLikeStripeSessionId(sessionId)) {
      setState(QS_UI_STATES.ERROR, {
        statusText: "Ongeldige betaalreferentie. Controleer mijn e-mail of telefoon opnieuw via de betaalpagina.",
        showRetry: false
      });
      renderErrorMessage(ui, "Deze link lijkt geen Stripe Checkout sessie te bevatten (cs_live_/cs_test_). Daardoor starten we geen scan.");
      pushDL("qs_result_invalid_session_id", { session_id: sessionId });
      return;
    }

    try {
      window.__IDS_QS_RESULT_RUNNING__ = window.__IDS_QS_RESULT_RUNNING__ || {};
      if (window.__IDS_QS_RESULT_RUNNING__[sessionId]) {
        bindResultButtons(ui, { retry: function () {} });
        var cachedOnce = readCachedNormalized(sessionId);
        if (cachedOnce && cachedOnce.report) {
          renderReport(ui, cachedOnce, { email: email, cfg: cfg });
          scheduleQsPurchase(sessionId, cfg, testMode);
          maybeAutoOpenSuccessOffer(ui, sessionId, cachedOnce.report);
          setState(QS_UI_STATES.DONE, { statusText: "Rapport geladen.", showRetry: true });
        } else {
          setState(QS_UI_STATES.PROCESSING, {
            statusText: "Je rapport wordt geladen…",
            loaderLine1: "Rapport laden",
            loaderLine2: "Even geduld — we halen het resultaat op.",
            showRetry: false
          });
        }
        return;
      }
      window.__IDS_QS_RESULT_RUNNING__[sessionId] = true;
    } catch (e) {}

    var targets = resolveWebhookTargets(cfg);
    if (!targets.ok) {
      setState(QS_UI_STATES.ERROR, { statusText: "Scanservice is tijdelijk niet beschikbaar.", showRetry: true });
      renderErrorMessage(ui, "De scanconfiguratie mist verplichte endpoints.");
      pushDL("qs_result_missing_webhooks", { session_id: sessionId, error: targets.error || "" });
      return;
    }

    var orchestratorUrl = targets.orchestratorUrl;
    var resultUrl = targets.resultUrl;
    var workerFallbackUrl = targets.workerFallbackUrl;

    if (ui.reportEmail && email) text(ui.reportEmail, maskEmail(email));

    var progressCtrl = null;
    var inFlight = false;
    var aborted = false;

    function startProgressIfNeeded() {
      if (!progressCtrl) progressCtrl = createProgressController(ui, TIMING.PROGRESS_EXPECTED_MS);
      return progressCtrl;
    }

    function stopProgressDone(msg) {
      if (progressCtrl && typeof progressCtrl.done === "function") progressCtrl.done(msg);
    }

    function stopProgressFail(msg) {
      if (progressCtrl && typeof progressCtrl.fail === "function") progressCtrl.fail(msg);
    }

    function renderAndPersist(normalized) {
      if (!normalized || !hasRenderableReport(normalized)) return false;

      writeCachedNormalized(sessionId, normalized);
      renderReport(ui, normalized, { email: email, cfg: cfg });
      scheduleQsPurchase(sessionId, cfg, testMode);

      stopProgressDone("Rapport geladen.");
      setState(QS_UI_STATES.DONE, { statusText: "Je rapport is klaar.", showRetry: true });

      clearLock(sessionId);

      var count = Number(
        (normalized.report && normalized.report.total_match_count) ||
          (normalized.meta && normalized.meta.total_match_count) ||
          (normalized.meta && normalized.meta.breach_count) ||
          0
      );

      pushDL("qs_report_rendered", {
        session_id: sessionId,
        severity: normalized.report.severity_code || "",
        count: count,
        test_mode: testMode
      });

      maybeAutoOpenSuccessOffer(ui, sessionId, normalized.report);

      tryLocalStorageRemove(STORAGE.intent);
      tryLocalStorageRemove(STORAGE.forceTrigger);
      tryLocalStorageRemove(STORAGE.phone);
      tryLocalStorageRemove(STORAGE.username);

      return true;
    }

    function failWithHttp(status) {
      var m = mapHttpToUserMessage(status);
      stopProgressFail(m.ui);

      setState(QS_UI_STATES.ERROR, {
        statusText: m.ui,
        loaderLine1: m.ui,
        loaderLine2: m.detail,
        showRetry: status !== 403 && status !== 202
      });

      renderErrorMessage(ui, m.detail);
      pushDL("qs_make_http_error", { session_id: sessionId, status: status, kind: m.kind });
    }

    function forceRetry() {
      if (inFlight) return;

      setState(QS_UI_STATES.PROCESSING, {
        statusText: "We halen je resultaat opnieuw op…",
        loaderLine1: "Resultaat ophalen",
        loaderLine2: "De scan kan nog bezig zijn. We starten niets opnieuw.",
        showRetry: false
      });

      startProgressIfNeeded();

      inFlight = true;
      pollForReport(true)
        .then(function (normalizedFromPoll) {
          if (!normalizedFromPoll) throw new Error("Nog geen rapport ontvangen.");
          renderAndPersist(normalizedFromPoll);
        })
        .catch(function (err) {
          console.error("[ids-quickscan] retry poll error:", err);

          stopProgressFail("Het rapport is nog niet beschikbaar.");
          setState(QS_UI_STATES.ERROR, {
            statusText: "Je scan lijkt nog bezig of het resultaat is nog niet klaar.",
            loaderLine1: "Nog niet klaar",
            loaderLine2: "Wacht even en klik daarna opnieuw op 'Resultaat ophalen'.",
            showRetry: true
          });

          renderErrorMessage(ui, "Je scan is waarschijnlijk nog bezig. We hebben geen nieuwe scan gestart. Probeer het over 20–60 seconden opnieuw.");
          pushDL("qs_report_fetch_retry_error", { session_id: sessionId, message: String((err && err.message) || err || "") });
        })
        .finally(function () {
          inFlight = false;
        });
    }

    bindResultButtons(ui, { retry: forceRetry });

    var cached = readCachedNormalized(sessionId);
    if (cached && cached.report) {
      renderReport(ui, cached, { email: email, cfg: cfg });
      scheduleQsPurchase(sessionId, cfg, testMode);
      maybeAutoOpenSuccessOffer(ui, sessionId, cached.report);
      stopProgressDone("Rapport geladen.");
      setState(QS_UI_STATES.DONE, { statusText: "Rapport geladen.", showRetry: true });
      pushDL("qs_result_cache_hit", { session_id: sessionId });
      return;
    }

    setState(QS_UI_STATES.PAID, {
      statusText: "Betaling bevestigd. We starten je scan…",
      loaderLine1: "Betaling bevestigd",
      loaderLine2: "We starten je QuickScan en bouwen je rapport. Dit duurt meestal tot 2 minuten (bij grotere resultaten soms iets langer).",
      showRetry: false
    });
    startProgressIfNeeded();

    function tryImmediateTrigger() {
      var lock = getLock(sessionId);
      var ack = readTriggerAck(sessionId);

      if (lock && lockIsFresh(lock)) {
        setState(QS_UI_STATES.PROCESSING, {
          statusText: "Je scan wordt verwerkt. We halen het resultaat op…",
          loaderLine1: "Scan wordt verwerkt",
          loaderLine2: "We starten niets dubbel. We halen het resultaat op zodra het klaar is.",
          showRetry: false
        });
        return Promise.resolve({ skippedTrigger: true, reason: "fresh_lock" });
      }

      if (ack && triggerAckIsFresh(ack)) {
        setState(QS_UI_STATES.PROCESSING, {
          statusText: "Je scan loopt al. We halen alleen het resultaat op…",
          loaderLine1: "Resultaat ophalen",
          loaderLine2: "We gebruiken je bestaande scan en starten niets opnieuw.",
          showRetry: false
        });
        setLock(sessionId, { stage: "ack_present_poll_only", session_id: sessionId, job_id: String(ack.job_id || "") });
        return Promise.resolve({ skippedTrigger: true, reason: "fresh_ack" });
      }

      setLock(sessionId, { stage: "trigger_start", session_id: sessionId });

      var payload = buildTriggerPayload(cfg, sessionId, { email: email, phone: phone, username: username });

      setState(QS_UI_STATES.SCANNING, {
        statusText: "We starten je scan…",
        loaderLine1: "QuickScan gestart",
        loaderLine2: "We controleren bekende bronnen en bouwen je rapport.",
        showRetry: false
      });

      pushDL("qs_make_trigger_start", {
        session_id: sessionId,
        test_mode: testMode,
        has_phone: !!phone,
        has_username: !!username
      });

      return postWebhook(orchestratorUrl, workerFallbackUrl, payload).then(function (res) {
        if (res.status === 202 || res.status === 403 || res.status === 400) {
          return { fatalHttp: res.status, triggerResponse: res };
        }

        var ackInfo = extractServerAckInfo(res);
        var effectiveJobId = String(ackInfo.job_id || payload.job_id || "").trim() || payload.job_id;

        setLock(sessionId, {
          stage: "trigger_response",
          session_id: sessionId,
          job_id: effectiveJobId,
          http_status: res.status,
          ack_status: ackInfo.status || ""
        });

        writeTriggerAck(sessionId, {
          ts: now(),
          accepted: ackInfo.accepted || !!res.ok,
          already_started: ackInfo.already_started,
          status: ackInfo.status || (res.ok ? "accepted" : ""),
          status_message: ackInfo.status_message || "",
          job_id: effectiveJobId,
          http_status: res.status,
          source: "trigger_response"
        });

        writeSessionMeta(sessionId, {
          trigger_http_status: res.status,
          trigger_ack_status: ackInfo.status || "",
          job_id: effectiveJobId
        });

        if (ackInfo && ackInfo.status_message) applyBackendStatusMessage(ui, ackInfo.status_message);

        var normalized = extractNormalizedFromResponseEnvelope(res, email);
        if (normalized && hasRenderableReport(normalized)) return { normalized: normalized, immediate: true, triggerResponse: res };

        return { normalized: null, immediate: false, triggerResponse: res };
      });
    }

    function pollForReport(isManualRetry) {
      var started = now();
      var errorStreak = 0;
      var pollSeq = 0;
      var lastBackendMsg = "";
      var lastBackendMsgTs = 0;

      function maybeApplyMsg(msg) {
        msg = String(msg || "").trim();
        if (!msg) return;
        var t = now();
        if (msg === lastBackendMsg && t - lastBackendMsgTs < 8000) return;
        lastBackendMsg = msg;
        lastBackendMsgTs = t;
        applyBackendStatusMessage(ui, msg);
      }

      function scheduleNext(fn) {
        var elapsed = now() - started;
        var delay = computePollDelayMs(elapsed, errorStreak);
        setTimeout(fn, delay);
      }

      function tick() {
        if (aborted) return Promise.reject(new Error("aborted"));

        var cachedNow = readCachedNormalized(sessionId);
        if (cachedNow && cachedNow.report) return Promise.resolve(cachedNow);

        var elapsed = now() - started;
        if (elapsed > TIMING.POLL_TOTAL_MS) return Promise.reject(new Error("Het rapport duurt langer dan verwacht."));

        setState(QS_UI_STATES.PROCESSING, {
          statusText: isManualRetry ? "Resultaat opnieuw ophalen…" : "Je rapport wordt opgebouwd…",
          loaderLine1: "Rapport opbouwen",
          loaderLine2: "Blijf op deze pagina. Je rapport verschijnt automatisch zodra het klaar is.",
          showRetry: false
        });

        pollSeq += 1;
        var pollPayload = buildPollPayload(cfg, sessionId, email, pollSeq);

        return postWebhook(resultUrl, workerFallbackUrl, pollPayload)
          .then(function (res) {
            if (res.status === 202 || res.status === 403 || res.status === 400) {
              throw Object.assign(new Error("http_" + res.status), { http_status: res.status });
            }

            var ackInfo = extractServerAckInfo(res);
            if (ackInfo && (ackInfo.job_id || ackInfo.status)) {
              writeTriggerAck(sessionId, {
                ts: now(),
                accepted: true,
                status: ackInfo.status || "",
                status_message: ackInfo.status_message || "",
                job_id: String(ackInfo.job_id || ""),
                source: "poll_response"
              });
              writeSessionMeta(sessionId, {
                last_poll_http_status: res.status,
                last_poll_status: ackInfo.status || "",
                job_id: String(ackInfo.job_id || "")
              });
            }

            var normalized = extractNormalizedFromResponseEnvelope(res, email);
            if (normalized && hasRenderableReport(normalized)) return normalized;

            var body = res.json;
            var statusHint = "";
            if (isObj(body)) statusHint = String(body.status_message || body.message || "").trim();
            if (!statusHint && ackInfo && ackInfo.status_message) statusHint = String(ackInfo.status_message || "").trim();
            if (statusHint) maybeApplyMsg(statusHint);

            errorStreak = 0;

            return new Promise(function (resolve, reject) {
              scheduleNext(function () {
                tick().then(resolve).catch(reject);
              });
            });
          })
          .catch(function (err) {
            if (err && err.http_status) {
              failWithHttp(Number(err.http_status));
              return Promise.reject(err);
            }

            errorStreak += 1;
            if (errorStreak === 1 || errorStreak % 3 === 0) console.warn("[ids-quickscan] poll error, retrying (" + errorStreak + ")");
            return new Promise(function (resolve, reject) {
              scheduleNext(function () {
                tick().then(resolve).catch(reject);
              });
            });
          });
      }

      return tick();
    }

    function run() {
      if (inFlight) return;
      inFlight = true;

      tryImmediateTrigger()
        .then(function (result) {
          if (result && result.fatalHttp) {
            failWithHttp(Number(result.fatalHttp));
            throw Object.assign(new Error("fatal_http_" + result.fatalHttp), { http_status: result.fatalHttp });
          }
          if (result && result.normalized) {
            renderAndPersist(result.normalized);
            return null;
          }
          return pollForReport(false);
        })
        .then(function (normalizedFromPoll) {
          if (!normalizedFromPoll) return;
          renderAndPersist(normalizedFromPoll);
        })
        .catch(function (err) {
          if (err && err.http_status) {
            return;
          }

          console.error("[ids-quickscan] result flow error:", err);

          stopProgressFail("Het laden van je rapport duurde te lang of mislukte.");
          setState(QS_UI_STATES.ERROR, {
            statusText: "We konden je rapport nog niet ophalen.",
            loaderLine1: "Rapport nog niet beschikbaar",
            loaderLine2: "Je scan kan nog bezig zijn. Wacht even en klik daarna op 'Resultaat opnieuw ophalen'.",
            showRetry: true
          });

          renderErrorMessage(ui, "Je scan is waarschijnlijk nog bezig. We starten niets opnieuw. Wacht 20–60 seconden en klik dan op 'Resultaat opnieuw ophalen'.");
          pushDL("qs_report_fetch_error", { session_id: sessionId, message: String((err && err.message) || err || "") });

          try {
            var lock = getLock(sessionId);
            if (lock && lockIsFresh(lock)) {
              setLock(sessionId, Object.assign({}, lock, { stage: "timeout_or_error", last_error: String((err && err.message) || err || "") }));
            }
          } catch (e) {}
        })
        .finally(function () {
          inFlight = false;
        });
    }

    run();
  }

  // ------------------------------------------------------------
  // RESULT MODALS + COPY (delegated, per section root)
  // ------------------------------------------------------------
  function bindResultModalsAndCopy(root) {
    if (!root) return;
    if (root.dataset.idsQsrModalBound === "1") return;
    root.dataset.idsQsrModalBound = "1";

    var focusMemory = Object.create(null);

    function focusSafe(el) {
      try {
        if (el && typeof el.focus === "function") el.focus();
      } catch (e) {}
    }

    function rememberFocus(id, triggerEl) {
      if (!id || !triggerEl) return;
      focusMemory[id] = triggerEl;
    }

    function restoreFocus(id) {
      if (!id) return;
      var el = focusMemory[id];
      if (!el) return;
      focusSafe(el);
    }

    qsa(root, "dialog.ids-qsr__modal").forEach(function (dlg) {
      if (dlg.dataset.idsDialogBound === "1") return;
      dlg.dataset.idsDialogBound = "1";

      dlg.addEventListener("close", function () {
        restoreFocus(dlg.id || "");
        pushDL("qs_modal_close", { modal_id: dlg.id || "" });
      });
    });

    function openDialogById(id, triggerEl) {
      if (!id) return;
      var dlg = byId(id);
      if (!dlg) return;

      rememberFocus(id, triggerEl || document.activeElement);

      if (typeof dlg.showModal === "function") {
        try {
          dlg.showModal();
          pushDL("qs_modal_open", { modal_id: id });
          return;
        } catch (e) {}
      }

      try {
        dlg.setAttribute("open", "open");
        pushDL("qs_modal_open", { modal_id: id });
      } catch (e2) {}
    }

    function closeDialogById(id) {
      if (!id) return;
      var dlg = byId(id);
      if (!dlg) return;

      if (typeof dlg.close === "function") {
        try {
          dlg.close();
          return;
        } catch (e) {}
      }

      try {
        dlg.removeAttribute("open");
        restoreFocus(id);
      } catch (e2) {}
    }

    function copyTextToClipboard(textToCopy, doneCb) {
      var s = String(textToCopy || "");
      if (!s) return;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(s)
          .then(function () {
            if (doneCb) doneCb(true);
          })
          .catch(function () {
            if (doneCb) doneCb(false);
          });
        return;
      }

      try {
        var ta = document.createElement("textarea");
        ta.value = s;
        ta.setAttribute("readonly", "readonly");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        if (doneCb) doneCb(true);
      } catch (e) {
        if (doneCb) doneCb(false);
      }
    }

    root.addEventListener(
      "click",
      function (e) {
        var t = e.target;

        var openBtn = t && t.closest ? t.closest("[data-ids-open-modal]") : null;
        if (openBtn) {
          var openId = openBtn.getAttribute("data-ids-open-modal");
          if (openId) {
            e.preventDefault();
            openDialogById(openId, openBtn);
          }
          return;
        }

        var closeBtn = t && t.closest ? t.closest("[data-ids-close-modal]") : null;
        if (closeBtn) {
          var closeId = closeBtn.getAttribute("data-ids-close-modal");
          if (closeId) {
            e.preventDefault();
            closeDialogById(closeId);
          }
          return;
        }

        if (t && t.tagName && String(t.tagName).toLowerCase() === "dialog") {
          try {
            var modalId = t.id || "";
            if (typeof t.close === "function") t.close();
            else t.removeAttribute("open");
            restoreFocus(modalId);
          } catch (e2) {}
          return;
        }

        var copyBtn = t && t.closest ? t.closest("[data-ids-copy-btn]") : null;
        if (copyBtn) {
          var val = copyBtn.getAttribute("data-ids-copy-value") || "";
          if (!val) return;

          e.preventDefault();
          copyTextToClipboard(val, function (ok) {
            if (!ok) return;
            try {
              copyBtn.setAttribute("data-copied", "1");
              pushDL("qs_copy_code_success", {});
              window.setTimeout(function () {
                try {
                  copyBtn.removeAttribute("data-copied");
                } catch (e3) {}
              }, 1400);
            } catch (e4) {}
          });
        }
      },
      true
    );
  }

  // ------------------------------------------------------------
  // PAGE ROUTER
  // ------------------------------------------------------------
  function inferPageType(root, ui) {
    var explicit = String(root.getAttribute("data-ids-page") || "").toLowerCase();
    if (explicit) return explicit;

    var path = String(window.location.pathname || "").toLowerCase();
    var hasSession = !!getSessionIdFromUrl();
    var hasReportHooks = !!(ui.reportRoot || ui.reportShell || ui.loadingShell || ui.reportHeadline || ui.reportBreaches);

    if (path.indexOf("quickscan-result") >= 0) return "quickscan-result";
    if (hasSession && hasReportHooks) return "quickscan-result";
    if (hasReportHooks && path.indexOf("quickscan") >= 0) return "quickscan-result";

    return "quickscan";
  }

  function initRoot(root) {
    if (!root) return;
    if (root.dataset.idsQsInitialized === "1") return;
    root.dataset.idsQsInitialized = "1";

    cleanupStaleIntentIfNeeded();

    var cfg = parseConfig(root);
    var ui = resolveUI(root, cfg);

    bindResultModalsAndCopy(root);

    var pageType = inferPageType(root, ui);

    try {
      if (pageType === "quickscan-result") initResultPage(root, cfg, ui);
      else initLandingPage(root, cfg, ui);
    } catch (e) {
      console.error("[ids-quickscan] init error:", e);
    }
  }

  function boot() {
    qsa(document, "[data-ids-qs]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (evt) {
    var container = evt && evt.target ? evt.target : document;
    qsa(container, "[data-ids-qs]").forEach(function (root) {
      root.dataset.idsQsInitialized = "0";
      initRoot(root);
    });
  });
})();